import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import React__default, { useReducer, useMemo, useEffect, useState, useCallback, memo, useRef } from 'react';
import classnames from 'classnames';
import { Tooltip, Util, Button, RichTextArea, RadioButton } from '@grandvisionhq/common';
import { subDays, addDays, getMonth, getYear, isValid } from 'date-fns';
import { Location } from '@grandvisionhq/api-client';
import { Container, Heading, TextArea as TextArea$1 } from '@grandvisionhq/elements';
import { Icon } from '@grandvisionhq/icons';
import { useIntl, useApiConfig } from '@grandvisionhq/state';
import { DateUtils, createScriptTag, injectTags } from '@grandvisionhq/utils';

const REGEX = {
    EMAIL_ADDRESS: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    TEXT: /^[a-zA-Z\u00C0-\u00ff .-]*$/,
    ADDRESS_LINE: /^[a-zA-Z\u00C0-\u00ff\u2018\u20190-9 .,'’’-]*$/,
    ALPHANUMERIC: /^[a-zA-Z0-9 ]*$/,
    NOT_LEADING_WHITESPACE: /^[^\s]+(\s.*)?$/,
    CONTAINS_DOT: /\./,
    BE: /^(?:(?:[1-9])(?:\d{3}))$/,
    NL: /^[1-9][0-9]{3} ?(?!sa|sd|ss)[a-z]{2}$/i,
    GB: /^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/i,
    IT: /^\d{5}$/,
    DK: /^(?:[1-24-9]\d{3}|3[0-8]\d{2})$/,
    SE: /^\d{3}\s?\d{2}/,
    PT: /^\d{4}-\d{3}$/,
    IE: /(?:^[AC-FHKNPRTV-Y][0-9]{2}|D6W)[ ]?[0-9AC-FHKNPRTV-Y]{4}$/,
    ES: /^\d{5}$/,
    FR: /^(0[1-9]|[1-8]\d|9[0-8])\d{2,3}$/
};
const DATES_RANGE = {
    DAY: {
        min: 1,
        max: 31
    },
    MONTH: {
        min: 1,
        max: 12
    },
    YEAR_OF_BIRTH: {
        min: 1900,
        get max () {
            return new Date().getFullYear();
        }
    }
};
const isRequired = (value)=>{
    return value !== '';
};
const getPostalCodeRegex = (country)=>REGEX[country]
;
const isValidPostalCodeForCountry = (country, postalCode)=>{
    const regex = getPostalCodeRegex(country);
    return regex !== undefined && regex.test(postalCode);
};
const isNumber = (value)=>{
    const parsed = parseFloat(value);
    return !isNaN(parsed) && isFinite(parsed);
};
const isInteger = (value)=>{
    const parsed = parseInt(value);
    return Number.isInteger(parsed);
};
const isPositiveInteger = (value)=>{
    return isInteger(value) && Number(value) > 0;
};
const isNotDecimalNumber = (value)=>{
    return !REGEX.CONTAINS_DOT.test(value) && isPositiveInteger(value);
};
const isText = (value)=>{
    return REGEX.TEXT.test(value);
};
const isAddressLine = (value)=>{
    return REGEX.ADDRESS_LINE.test(value);
};
const isAlphanumeric = (value)=>{
    return REGEX.ALPHANUMERIC.test(value);
};
const isEmailAddress = (value)=>{
    return REGEX.EMAIL_ADDRESS.test(value);
};
const isInRange = (value, from, to)=>{
    return value >= from && value <= to;
};
const isValidMonth = (value)=>{
    return isNumber(value) && isInRange(Number(value), DATES_RANGE.MONTH.min, DATES_RANGE.MONTH.max);
};
const isValidDay = (value)=>{
    return isNumber(value) && isInRange(Number(value), DATES_RANGE.DAY.min, DATES_RANGE.DAY.max);
};
const isValidBirthDateYear = (value)=>{
    return isNumber(value) && isInRange(Number(value), DATES_RANGE.YEAR_OF_BIRTH.min, DATES_RANGE.YEAR_OF_BIRTH.max);
};
const isDate = (value)=>{
    const date = value.split('-');
    return isValidDate(date[2], date[1], date[0]);
};
const isDateOfBirth = (value)=>{
    const [yyyy, mm, dd] = value.split('-');
    if (!yyyy || !mm || !dd) return false;
    return isValidBirthDate(dd, mm, yyyy);
};
const isValidDate = (dd, mm, yyyy)=>{
    const dateObject = new Date(`${yyyy}/${mm}/${dd}`);
    return !isNaN(dateObject.getTime()) && dateObject.getDate() === parseInt(dd);
};
const isValidBirthDate = (dd, mm, yyyy)=>{
    return isValidDate(dd, mm, yyyy) && isValidBirthDateYear(yyyy);
};
const isOlderThan = (ageRestriction)=>(value)=>{
        const today = new Date();
        const birthDate = new Date(value.replace(/-/g, '/')) // replace '-' with '/' because safari can't take it.
        ;
        const monthsLeft = today.getMonth() - birthDate.getMonth();
        let age = today.getFullYear() - birthDate.getFullYear();
        if (monthsLeft < 0 || monthsLeft === 0 && today.getDate() < birthDate.getDate()) {
            age--;
        }
        return age >= ageRestriction;
    }
;
// Min/Max/Exact length string validators
const minLength$1 = (minLength1)=>(value)=>{
        return typeof value === 'string' && value.length >= minLength1;
    }
;
const maxLength = (maxLength1)=>(value)=>{
        return typeof value === 'string' && value.length <= maxLength1;
    }
;
const exactLength = (length)=>(value)=>{
        return typeof value === 'string' && value.length === length;
    }
;
const isNotLeadingWhitespace = (value)=>{
    return REGEX.NOT_LEADING_WHITESPACE.test(value);
};

var validationsUtil = /*#__PURE__*/Object.freeze({
  __proto__: null,
  DATES_RANGE: DATES_RANGE,
  isRequired: isRequired,
  getPostalCodeRegex: getPostalCodeRegex,
  isValidPostalCodeForCountry: isValidPostalCodeForCountry,
  isNumber: isNumber,
  isInteger: isInteger,
  isPositiveInteger: isPositiveInteger,
  isNotDecimalNumber: isNotDecimalNumber,
  isText: isText,
  isAddressLine: isAddressLine,
  isAlphanumeric: isAlphanumeric,
  isEmailAddress: isEmailAddress,
  isInRange: isInRange,
  isValidMonth: isValidMonth,
  isValidDay: isValidDay,
  isValidBirthDateYear: isValidBirthDateYear,
  isDate: isDate,
  isDateOfBirth: isDateOfBirth,
  isValidDate: isValidDate,
  isValidBirthDate: isValidBirthDate,
  isOlderThan: isOlderThan,
  minLength: minLength$1,
  maxLength: maxLength,
  exactLength: exactLength,
  isNotLeadingWhitespace: isNotLeadingWhitespace
});

/**
 * Substracts number of {days} from the provided {date}
 *
 * @example
 *   const minDate = createMinDate(new Date(), 5)
 *   <DatePicker minDate={minDate} />
 */ const createMinDate = (date, days)=>{
    return date instanceof Date && typeof days === 'number' ? subDays(date, days) : undefined;
};
/**
 * Add number of {days} to the provided {date}
 *
 * @example
 *   const maxDate = createMaxDate(new Date(), 10)
 *   <DatePicker maxDate={maxDate} />
 */ const createMaxDate = (date, days)=>{
    return date instanceof Date && typeof days === 'number' ? addDays(date, days) : undefined;
};

const getValuesFromState = (state)=>{
    return Object.entries(state).reduce((result, [fieldName, fieldState])=>Object.assign(result, {
            [fieldName]: fieldState ? fieldState.value : ''
        })
    , {});
};
const getValidityFromState = (state)=>{
    for(const field in state){
        if (state.hasOwnProperty(field)) {
            const fieldState = state[field];
            if (fieldState && !fieldState.readonly && fieldState.isValid === false) {
                return false;
            }
        }
    }
    return true;
};
const getValuesFromComposedState = (composedState)=>Object.entries(composedState).reduce((acc, [key, state])=>swcHelpers.objectSpread({}, acc, {
            [key]: getValuesFromState(state)
        })
    , {})
;
const isValidComposedState = (state)=>Object.values(state).every(getValidityFromState)
;

/**
 * Marks all the invalid fields in a form as touched.
 */ function markFormFieldsTouched(form) {
    return Object.entries(form).reduce((prev, [field, valueState])=>{
        if (!valueState) {
            return prev;
        }
        if (valueState.isValid) {
            return swcHelpers.objectSpread({}, prev, {
                [field]: valueState
            });
        }
        return swcHelpers.objectSpread({}, prev, {
            [field]: swcHelpers.objectSpread({}, valueState, {
                isTouched: true
            })
        });
    }, {});
}

const objectToFormState = (data)=>{
    return Object.entries(data).reduce((acc, [formKey, pairs])=>{
        if (!pairs) {
            return {};
        }
        const newFormState = Object.entries(pairs).reduce((formValueState, [key, value])=>{
            return typeof value !== 'undefined' ? swcHelpers.objectSpread({}, formValueState, {
                [key]: {
                    name: key,
                    value,
                    isTouched: false
                }
            }) : formValueState;
        }, {});
        return swcHelpers.objectSpread({}, acc, {
            [formKey]: swcHelpers.objectSpread({}, acc[formKey], {
                formName: formKey,
                formValid: false,
                formState: swcHelpers.objectSpread({}, (acc[formKey] || {}).formState, newFormState)
            })
        });
    }, {});
};
const fieldsToFormState = (data, defaults)=>Object.entries(data).reduce((result, [name, value])=>swcHelpers.objectSpread({}, result, {
            [name]: swcHelpers.objectSpread({
                name,
                value: String(value),
                isTouched: false
            }, defaults)
        })
    , {})
;

/*
  Helper function to create SelectOptions from an array of labels
 */ const createSimpleSelectOptions = (labels)=>{
    return labels.reduce((result, label)=>{
        result[label] = {
            label,
            value: label
        };
        return result;
    }, {});
};

/**
 * Helper function to convert an array of objects in to an object that is
 * formatted correctly for the Select component
 * @param arr - Array of objects that will be turned into an object of objects
 * @param key - string that is used as key to lookup the value that will be used as labels(keys) in the returned object
 */ function createSelectOptions(arr = [], key) {
    return arr.reduce((acc, curr)=>swcHelpers.objectSpread({}, acc, {
            [String(curr[key])]: curr
        })
    , {});
}

/**
 * Merges two ComposedFormValueState objects. When a field occurs in the same
 * part in both composed states, the field's state from `b` is used.
 */ const mergeComposedFormValueState = (a, b)=>Object.entries(b).reduce((result, [partKey, partState])=>swcHelpers.objectSpread({}, result, {
            [partKey]: swcHelpers.objectSpread({}, result[partKey], partState)
        })
    , swcHelpers.objectSpread({}, a))
;

const addLeadingZero = (value)=>value && value.padStart(2, '0')
;
const toStandardDateFormat = (date)=>{
    if (!date) {
        return '';
    }
    const [year, month, day] = date.split('-');
    return `${year}-${addLeadingZero(month)}-${addLeadingZero(day)}`;
};

var index = /*#__PURE__*/Object.freeze({
  __proto__: null,
  validations: validationsUtil,
  createMinDate: createMinDate,
  createMaxDate: createMaxDate,
  getValuesFromState: getValuesFromState,
  getValidityFromState: getValidityFromState,
  getValuesFromComposedState: getValuesFromComposedState,
  isValidComposedState: isValidComposedState,
  markFormFieldsTouched: markFormFieldsTouched,
  objectToFormState: objectToFormState,
  fieldsToFormState: fieldsToFormState,
  createSimpleSelectOptions: createSimpleSelectOptions,
  createSelectOptions: createSelectOptions,
  mergeComposedFormValueState: mergeComposedFormValueState,
  addLeadingZero: addLeadingZero,
  toStandardDateFormat: toStandardDateFormat
});

const FormContext = /*#__PURE__*/ React.createContext({});
FormContext.displayName = 'FormContext';
const useFormContext = ()=>{
    const context = React.useContext(FormContext);
    if (!context) {
        throw new Error('No FormContext available. You might still need to wrap the application with the FormProvider component.');
    }
    return context;
};
class Form extends React.Component {
    UNSAFE_componentWillReceiveProps(nextProps) {
        if (nextProps.initialFormState && nextProps.initialFormState !== this.props.initialFormState) {
            this.setState({
                formState: nextProps.initialFormState
            });
        }
    }
    render() {
        const { className ='form' , name , 'data-t': dataT , showRequiredAndOptionalLabels =true ,  } = this.props;
        const context = {
            name,
            onBlur: this.onBlur,
            onChange: this.onChange,
            onSubmit: this.onSubmit,
            getInitialState: this.getInitialState,
            showRequiredAndOptionalLabels
        };
        return /*#__PURE__*/ React.createElement(FormContext.Provider, {
            value: context
        }, /*#__PURE__*/ React.createElement("form", {
            id: name,
            className: className,
            "data-t": dataT || name,
            "aria-label": this.props['aria-label'],
            onSubmit: this.handleSubmit,
            noValidate: true
        }, this.props.children));
    }
    constructor(props){
        super(props);
        this.handleState = (cb, formControlState)=>{
            this.setState((prevState)=>{
                const newFormState = swcHelpers.objectSpread({}, prevState.formState, {
                    [formControlState.name]: swcHelpers.objectSpread({}, formControlState)
                });
                return {
                    formState: swcHelpers.objectSpread({}, newFormState),
                    formValid: getValidityFromState(newFormState)
                };
            }, cb);
        };
        this.handleChange = ()=>{
            this.props.onChange && this.props.onChange(this.state);
        };
        this.handleBlur = ()=>{
            this.props.onBlur && this.props.onBlur(this.state);
        };
        this.handleSubmit = (e)=>{
            const { onSubmit , showErrorsOnInvalidSubmit =true  } = this.props;
            if (e) {
                e.preventDefault();
            }
            if (showErrorsOnInvalidSubmit && !this.state.formValid) {
                return this.setState({
                    formState: markFormFieldsTouched(this.state.formState)
                });
            }
            onSubmit && onSubmit(this.state);
        };
        this.onBlur = this.handleState.bind(this, this.handleBlur);
        this.onChange = this.handleState.bind(this, this.handleChange);
        this.onSubmit = this.handleState.bind(this, this.handleSubmit);
        this.getInitialState = (key)=>this.state.formState[key]
        ;
        this.state = {
            formName: props.name,
            formState: props.initialFormState || {},
            formValid: false
        };
    }
}

class Input extends (React.Component) {
    componentDidUpdate(prevProps, prevState) {
        if (prevProps.initialValue !== this.props.initialValue) {
            this.setState({
                value: this.props.initialValue || ''
            });
        }
        if (prevState.value !== this.state.value) {
            this.onChange();
        }
    }
    render() {
        const _props = this.props, { type , className , 'data-t': dataT , id , inputRef , max , min , // Omit initialValue from the input props
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        initialValue  } = _props, rest = swcHelpers.objectWithoutProperties(_props, [
            "type",
            "className",
            'data-t',
            "id",
            "inputRef",
            "max",
            "min",
            "initialValue"
        ]);
        const { value  } = this.state;
        return /*#__PURE__*/ React.createElement("input", swcHelpers.extends({}, rest, {
            name: id,
            type: type,
            className: className,
            "data-t": dataT,
            value: value,
            onChange: this.handleChange,
            onBlur: this.handleBlur,
            min: type === 'number' ? min : undefined,
            max: type === 'number' ? max : undefined,
            ref: inputRef
        }));
    }
    constructor(...args){
        super(...args);
        this.state = {
            value: this.props.initialValue,
            isTouched: false
        };
        this.handleChange = (event)=>{
            const value = event.currentTarget.value;
            if (value !== this.state.value) {
                this.setState({
                    value
                });
            }
            event.preventDefault();
        };
        this.onChange = ()=>{
            if (this.props.onChange) {
                this.props.onChange(this.state);
            }
        };
        this.handleBlur = ()=>{
            if (!this.state.isTouched) {
                this.setState({
                    isTouched: true
                }, this.onBlur);
            } else {
                this.onBlur();
            }
        };
        this.onBlur = ()=>{
            if (this.props.onBlur) {
                this.props.onBlur(this.state);
            }
        };
    }
}
Input.defaultProps = {
    id: '',
    'data-t': '',
    type: 'text',
    placeholder: undefined,
    initialValue: '',
    disabled: false
};

const isEqualState$1 = (a, b)=>a.value === b.value && a.isTouched === b.isTouched && a.isValid === b.isValid && a.readonly === b.readonly
;
class FormControl extends (React.Component) {
    static getDerivedStateFromProps(props, state) {
        return {
            isValid: FormControl.validate(props.validations, props.required || false, state.value || '', props.maxLength)
        };
    }
    componentDidMount() {
        const initialState = this.props.initialState;
        if (initialState) {
            this.setState({
                value: initialState.value || '',
                isTouched: initialState.isTouched !== undefined && initialState.isTouched,
                isValid: FormControl.validate(this.props.validations, this.props.required || false, initialState.value)
            });
            if (initialState.readonly) {
                this.setState({
                    readonly: true
                });
            }
        }
    }
    applyInitialState(initialState) {
        this.setState({
            value: initialState.value || '',
            isTouched: initialState.isTouched !== undefined && initialState.isTouched,
            isValid: FormControl.validate(this.props.validations, this.props.required || false, initialState.value)
        });
    }
    componentDidUpdate(prevProps, prevState) {
        if (prevProps.initialState && this.props.initialState && !isEqualState$1(prevProps.initialState, this.props.initialState)) {
            this.applyInitialState(this.props.initialState);
        }
        if (prevState.isTouched !== this.state.isTouched || prevState.isValid !== this.state.isValid || prevState.value !== this.state.value) {
            this.onChange();
        }
    }
    render() {
        const _props = this.props, { label , id , type , icon , errorMessage , required , placeholder , optionalText , tooltip , initialState , disabled , className , maxLength: maxLength1 , hideAsteriskSign  } = _props, rest = swcHelpers.objectWithoutProperties(_props, [
            "label",
            "id",
            "type",
            "icon",
            "errorMessage",
            "required",
            "placeholder",
            "optionalText",
            "tooltip",
            "initialState",
            "disabled",
            "className",
            "maxLength",
            "hideAsteriskSign"
        ]);
        const { isValid , isTouched , value , readonly  } = this.state;
        const displayValid = isValid === true && isTouched === true && value !== '' && !readonly;
        const displayError = isValid === false && isTouched === true && !readonly;
        const errorMessageId = `${id}-error-message`;
        const ariaDescribedBy = `${rest['aria-describedby'] || ''} ${errorMessageId}`.trim();
        const inputClasses = classnames('form-control__input', {
            'form-control__input--with-aside': icon,
            _is_valid: displayValid,
            _is_invalid: displayError
        });
        return /*#__PURE__*/ React.createElement("label", {
            className: classnames('form-control', className)
        }, label && /*#__PURE__*/ React.createElement("span", {
            className: "form-control__label"
        }, label, required && !hideAsteriskSign && /*#__PURE__*/ React.createElement("span", {
            className: "form-control__asterisk"
        }, "*"), tooltip && /*#__PURE__*/ React.createElement(Tooltip, {
            message: tooltip,
            id: id
        })), !required && optionalText && /*#__PURE__*/ React.createElement("small", {
            className: "form-control__optional-tag"
        }, optionalText), /*#__PURE__*/ React.createElement("div", {
            className: "form-control__wrapper"
        }, /*#__PURE__*/ React.createElement(Input, {
            "aria-invalid": !isValid,
            "aria-required": required,
            "aria-describedby": ariaDescribedBy,
            id: id,
            className: inputClasses,
            "data-t": this.props['data-t'],
            type: type,
            initialValue: initialState && initialState.value || '',
            placeholder: placeholder,
            onChange: this.handleChange,
            onBlur: this.handleBlur,
            disabled: disabled || readonly,
            maxLength: maxLength1
        }), icon && /*#__PURE__*/ React.createElement("div", {
            className: "form-control__aside"
        }, icon)), /*#__PURE__*/ React.createElement("span", {
            id: errorMessageId,
            className: "form-control__error"
        }, errorMessage && displayError && /*#__PURE__*/ React.createElement(React.Fragment, null, errorMessage)));
    }
    constructor(...args){
        super(...args);
        this.state = {
            name: this.props.id
        };
        this.onChange = ()=>{
            if (this.props.onChange) {
                this.props.onChange(swcHelpers.objectSpread({}, this.state, {
                    isValid: FormControl.validate(this.props.validations, this.props.required, this.state.value)
                }));
            }
        };
        this.handleChange = (state)=>{
            const value = state.value;
            if (value !== this.state.value) {
                this.setState({
                    value
                });
            }
        };
        this.handleBlur = (state)=>{
            const value = state.value.trim();
            this.setState({
                isTouched: state.isTouched,
                value
            }, ()=>{
                var _props, ref;
                (ref = (_props = this.props).onBlur) === null || ref === void 0 ? void 0 : ref.call(_props, this.state);
            });
        };
    }
}
FormControl.defaultProps = {
    type: 'text',
    hideAsteriskSign: false,
    required: true,
    validations: [],
    initialState: {
        value: '',
        isTouched: false
    },
    disabled: false
};
FormControl.validate = (validations, required, value, maxLengthValue)=>{
    if (!required && value.length === 0) {
        return true;
    }
    if (required) {
        if (!isRequired(value)) {
            return false;
        }
    }
    if (maxLengthValue && !maxLength(maxLengthValue)(value)) {
        return false;
    }
    return validations.reduce((isValid, validator)=>validator(value) && isValid
    , true);
};
const ConnectedFormControl = (_param)=> {
    var { onBlur , onChange  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "onBlur",
        "onChange"
    ]);
    return React.createElement(FormContext.Consumer, null, (context = {})=>/*#__PURE__*/ React.createElement(FormControl, swcHelpers.extends({}, rest, {
            onBlur: context.onBlur || onBlur,
            onChange: context.onChange || onChange,
            initialState: context.getInitialState && context.getInitialState(rest.id) || rest.initialState && rest.initialState
        }))
    );
};
ConnectedFormControl.defaultProps = swcHelpers.objectSpread({}, FormControl.defaultProps);

var DateFormat;
(function(DateFormat) {
    DateFormat["dmy"] = "dmy";
    DateFormat["ymd"] = "ymd";
    DateFormat["mdy"] = "mdy";
})(DateFormat || (DateFormat = {}));

var types = /*#__PURE__*/Object.freeze({
  __proto__: null,
  get DateFormat () { return DateFormat; }
});

const Suggestions = ({ className ='' , items =[] , onClick  })=>/*#__PURE__*/ React__default.createElement("ul", {
        className: `suggestions ${className}`,
        "aria-live": "polite"
    }, items.map((item)=>/*#__PURE__*/ React__default.createElement("li", {
            key: item.id,
            className: "suggestions__item"
        }, /*#__PURE__*/ React__default.createElement("button", {
            className: "suggestions__item-button",
            onClick: ()=>onClick(item)
            ,
            onKeyUp: (e)=>(e.keyCode === 13 || e.keyCode === 32) && onClick(item)
        }, item.label))
    ))
;

const getPlacePredictions = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(place1, url) {
        const { places  } = yield Location.getPlacePredictions(url, {
            place: place1
        });
        return places.map((place, index)=>{
            const [primaryText, secondaryText] = place;
            return {
                id: index.toString(),
                label: `${primaryText} ${secondaryText}`
            };
        });
    });
    return function getPlacePredictions(place1, url) {
        return _ref.apply(this, arguments);
    };
}();
const regHex = /#([a-f0-9]{3}){1,2}\b/i;
const regPresetBg = /black|grey|white/;
const BookAppointment = ({ link , inputField , title , items =[] , isLocationEnabled , isPlacePredictionsEnabled , imageUrl , text , backgroundColor , textColor ,  })=>{
    const { getLabel  } = useIntl();
    const { locationsUrl  } = useApiConfig();
    const [inputValue, setInputValue] = React.useState('');
    const [predictions, setPredictions] = React.useState(items);
    const [showPredictions, setShowPredictions] = React.useState(false);
    const [bookAppointmentUrl, setBookAppointmentUrl] = React.useState();
    const [currentLocationUrl, setCurrentLocationUrl] = React.useState();
    const ref1 = React.useRef(null);
    const onClickSuggestion = React.useCallback((item)=>{
        setInputValue(item.label);
        setShowPredictions(false);
    }, []);
    const onChange = React.useCallback((input)=>{
        setInputValue(input.currentTarget.value);
    }, []);
    const onFocus = React.useCallback(()=>{
        setShowPredictions(true);
    }, []);
    const onBlur = React.useCallback((event)=>{
        var ref;
        if (event.relatedTarget && ((ref = ref1.current) === null || ref === void 0 ? void 0 : ref.contains(event.relatedTarget))) {
            return;
        }
        setShowPredictions(false);
    }, []);
    React.useEffect(()=>{
        setBookAppointmentUrl(new URL(link.url, window.location.origin));
        setCurrentLocationUrl(new URL(link.url, window.location.origin));
    }, [
        link.url
    ]);
    React.useEffect(()=>{
        if (currentLocationUrl) {
            currentLocationUrl.searchParams.set('userLocation', 'true');
        }
    }, [
        currentLocationUrl
    ]);
    React.useEffect(()=>{
        if (Boolean(inputValue.trim()) && bookAppointmentUrl) {
            bookAppointmentUrl === null || bookAppointmentUrl === void 0 ? void 0 : bookAppointmentUrl.searchParams.set('search', inputValue);
        } else if (bookAppointmentUrl === null || bookAppointmentUrl === void 0 ? void 0 : bookAppointmentUrl.searchParams.has('search')) {
            bookAppointmentUrl === null || bookAppointmentUrl === void 0 ? void 0 : bookAppointmentUrl.searchParams.delete('search');
        }
        if (!isPlacePredictionsEnabled) {
            return;
        }
        if (inputValue.length < 3) {
            setPredictions([]);
            return;
        }
        let current = true;
        const timeout = setTimeout(()=>{
            if (current) {
                getPlacePredictions(inputValue, locationsUrl).then((result)=>current && setPredictions(result)
                ).catch((err)=>console.error(err)
                );
            }
        }, 400);
        return ()=>{
            current = false;
            clearTimeout(timeout);
        };
    }, [
        bookAppointmentUrl,
        inputValue,
        isPlacePredictionsEnabled,
        locationsUrl
    ]);
    const extended = !!imageUrl && !!text;
    const extendedBookAppointmentStyle = {};
    if (backgroundColor) {
        extendedBookAppointmentStyle.backgroundColor = regHex.test(backgroundColor) ? backgroundColor : regPresetBg.test(backgroundColor) ? Util.colorHexMap[backgroundColor] : 'transparent';
    }
    const textColorStyle = {};
    if (textColor) {
        textColorStyle.color = regHex.test(textColor) ? textColor : regPresetBg.test(textColor) ? Util.colorHexMap[textColor] : 'black';
    }
    return /*#__PURE__*/ React.createElement(Container, {
        className: classnames('book-appointment-container', {
            'book-appointment-container--extended': extended
        })
    }, extended && imageUrl && /*#__PURE__*/ React.createElement("img", {
        className: "book-appointment-image",
        src: imageUrl,
        alt: ""
    }), /*#__PURE__*/ React.createElement("div", {
        className: "book-appointment",
        style: extendedBookAppointmentStyle
    }, /*#__PURE__*/ React.createElement(Heading, {
        variant: "small",
        className: classnames('book-appointment__title', {
            'book-appointment__title--extended': extended
        }),
        style: textColorStyle
    }, title), /*#__PURE__*/ React.createElement("div", {
        className: "book-appointment__input",
        ref: ref1,
        onBlur: onBlur
    }, /*#__PURE__*/ React.createElement("input", {
        id: inputField.id,
        type: inputField.type,
        value: inputValue,
        onChange: onChange,
        onFocus: onFocus,
        className: "form-control__input",
        placeholder: inputField.placeholder
    }), isLocationEnabled && /*#__PURE__*/ React.createElement("a", {
        className: "book-appointment__use-gps",
        "aria-label": getLabel('stores.actions.useMyLocation'),
        href: currentLocationUrl ? currentLocationUrl.toString() : ''
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "gps"
    })), isPlacePredictionsEnabled && /*#__PURE__*/ React.createElement("div", {
        className: "book-appointment__suggestions-container",
        "data-t": "suggestions-container"
    }, showPredictions && /*#__PURE__*/ React.createElement(Suggestions, {
        className: classnames('book-appointment__suggestions', {
            _is_active: predictions.length > 0
        }),
        items: predictions,
        onClick: onClickSuggestion
    }))), /*#__PURE__*/ React.createElement(Button, {
        className: "book-appointment__button",
        caption: link.label,
        color: link.style || 'primary',
        href: bookAppointmentUrl ? bookAppointmentUrl.toString().replace(/\+/g, ' ') : ''
    }), extended && text && /*#__PURE__*/ React.createElement("div", {
        className: "book-appointment__content",
        style: textColorStyle
    }, /*#__PURE__*/ React.createElement(RichTextArea, {
        content: text,
        textColor: textColor === 'white' ? 'white' : 'black'
    }))));
};

const DiscountCodeForm = ({ onSubmit , content , buttonCaptionLabel , placeholderLabel , className , 'data-t': dataT ,  })=>{
    const [inputValue, setInputValue] = React.useState('');
    const [hasError, setHasError] = React.useState(false);
    if (!content) {
        return null;
    }
    const { type , inputId  } = content.inputField;
    const formClass = classnames('discount-code-form', {
        'discount-code-form--error': hasError
    });
    return /*#__PURE__*/ React.createElement("div", {
        className: formClass,
        onAnimationEnd: formAnimationEnd
    }, /*#__PURE__*/ React.createElement(Form, {
        name: "discount-code",
        className: classnames('discount-code-form__form', className),
        "data-t": dataT,
        onSubmit: handleOnSubmit,
        showErrorsOnInvalidSubmit: false
    }, /*#__PURE__*/ React.createElement(Input, {
        id: inputId,
        className: 'form-control__input discount-code-form__input',
        "data-t": dataT,
        type: type,
        initialValue: inputValue,
        placeholder: placeholderLabel,
        onChange: handleOnChange
    }), /*#__PURE__*/ React.createElement(Button, {
        size: "custom",
        className: "discount-code-form__button",
        caption: buttonCaptionLabel,
        onClick: handleOnSubmit,
        variant: "outline"
    })));
    function handleOnChange({ value  }) {
        setInputValue(value);
    }
    function handleOnSubmit() {
        if (!inputValue) {
            setHasError(true);
            return;
        }
        onSubmit(inputValue);
        setInputValue('');
    }
    function formAnimationEnd() {
        setHasError(false);
    }
};

const validate = (value, validations)=>{
    if (!validations) {
        return true;
    }
    return validations.reduce((isValid, validator)=>validator(value) && isValid
    , true);
};
const DefaultError = /*#__PURE__*/ React__default.memo(({ message , id , className  })=>/*#__PURE__*/ React__default.createElement("p", {
        id: id,
        className: classnames('field__error', className)
    }, message)
);
DefaultError.displayName = 'Memo(DefaultError)';
/*
 * <Field /> should always be used within a <Form />
 */ const Field = (_param)=>{
    var { children , name , id , label , icon , required , disabled , placeholder , error , className , validations  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "children",
        "name",
        "id",
        "label",
        "icon",
        "required",
        "disabled",
        "placeholder",
        "error",
        "className",
        "validations"
    ]);
    const resolvedId = id || name;
    const { getLabel  } = useIntl();
    const context = React__default.useContext(FormContext);
    const state = context.getInitialState && context.getInitialState(name) || {
        name,
        value: ''
    };
    const deps = [
        state.value,
        state.isValid,
        state.isTouched
    ];
    const handleChange = React__default.useCallback((value)=>{
        if (state.readonly) {
            return;
        }
        const updatedState = swcHelpers.objectSpread({}, state, {
            value,
            isValid: validate(value, validations)
        });
        context.onChange && context.onChange(updatedState);
        rest.onChange && rest.onChange(updatedState);
    }, [
        ...deps,
        validations,
        context.onChange,
        rest.onChange
    ]);
    const handleBlur = React__default.useCallback((value)=>{
        if (state.readonly) {
            return;
        }
        const updatedState = swcHelpers.objectSpread({}, state, {
            value,
            isTouched: true
        });
        context.onBlur && context.onBlur(updatedState);
        rest.onBlur && rest.onBlur(updatedState);
    }, [
        ...deps,
        context.onBlur,
        rest.onBlur
    ]);
    // To validate initial state value from context, on initial render and a change of validations
    React__default.useEffect(()=>{
        if (!context.onChange) {
            return;
        }
        const newValidationResult = validate(state.value, validations);
        // if the result of the validation of the value is the same as in context, skip update
        if (state.isValid !== newValidationResult) {
            context.onChange(swcHelpers.objectSpread({}, state, {
                isValid: newValidationResult
            }));
        }
    }, [
        validations,
        state.value,
        state.isValid,
        context.onChange
    ]);
    const { isValid , isTouched , readonly  } = state;
    const shouldDisplayValid = isValid === true && isTouched === true && state.value !== '' && !readonly;
    const shouldDisplayError = isValid === false && isTouched === true && !readonly;
    const childClasses = classnames({
        _is_valid: shouldDisplayValid,
        _has_error: shouldDisplayError
    });
    const { showRequiredAndOptionalLabels  } = context;
    const displayRequiredLabel = showRequiredAndOptionalLabels && required;
    const displayOptionalLabel = showRequiredAndOptionalLabels && !required;
    const errorContent = React__default.useMemo(()=>typeof error === 'string' ? shouldDisplayError && /*#__PURE__*/ React__default.createElement(DefaultError, {
            id: `${resolvedId}_error`,
            message: error
        }) : error(shouldDisplayError)
    , [
        shouldDisplayError,
        resolvedId,
        error
    ]);
    return /*#__PURE__*/ React__default.createElement("div", {
        className: classnames('field', className)
    }, label && /*#__PURE__*/ React__default.createElement("div", {
        className: "field__label-wrapper"
    }, /*#__PURE__*/ React__default.createElement("label", {
        htmlFor: resolvedId,
        className: "field__label"
    }, label, displayRequiredLabel && /*#__PURE__*/ React__default.createElement("span", {
        className: "field__required-asterisk"
    }, "*")), displayOptionalLabel && /*#__PURE__*/ React__default.createElement("small", {
        className: "field__optional-tag"
    }, getLabel('common.optional'))), /*#__PURE__*/ React__default.createElement("div", {
        className: "field__content"
    }, React__default.Children.map(children, (child)=>{
        return /*#__PURE__*/ React__default.cloneElement(child, {
            name,
            id: resolvedId,
            value: state.value,
            onChange: handleChange,
            onBlur: handleBlur,
            className: childClasses,
            required,
            disabled,
            placeholder
        });
    })), errorContent);
};

const CounterFooter = ({ count , errorMessage , id , showError  })=>{
    const { getFormattedLabel  } = useIntl();
    return /*#__PURE__*/ React__default.createElement("div", {
        className: "textarea-component__message"
    }, showError && /*#__PURE__*/ React__default.createElement(DefaultError, {
        id: `${id}_error`,
        className: "textarea-component__error",
        message: errorMessage
    }), count !== null && /*#__PURE__*/ React__default.createElement("p", {
        id: `${id}_count`,
        "aria-label": getFormattedLabel('fields.textarea.charactersRemaining', {
            labelData: {
                amount: count
            }
        }),
        className: classnames('textarea-component__count', {
            'textarea-component__count--error': count < 1
        })
    }, count));
};
CounterFooter.displayName = 'CounterFooter';
const TextArea = (_param)=>{
    var { errorMessage , name , id , label , placeholder , maxLength , validations , required , disabled , className , 'data-t': testId  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "errorMessage",
        "name",
        "id",
        "label",
        "placeholder",
        "maxLength",
        "validations",
        "required",
        "disabled",
        "className",
        'data-t'
    ]);
    const resolvedId = id || name;
    const initialCount = maxLength ? maxLength : null;
    const [count, setCount] = React__default.useState(initialCount);
    const handleEvent = React__default.useCallback((state)=>{
        maxLength && setCount(maxLength - state.value.length);
    }, [
        maxLength
    ]);
    const renderError = (showError)=>/*#__PURE__*/ React__default.createElement(CounterFooter, {
            id: resolvedId,
            count: count,
            errorMessage: errorMessage,
            showError: showError
        })
    ;
    return /*#__PURE__*/ React__default.createElement(Field, swcHelpers.extends({}, rest, {
        name: name,
        id: resolvedId,
        label: label,
        className: classnames('textarea-component', className),
        required: required,
        disabled: disabled,
        placeholder: placeholder,
        validations: validations,
        error: renderError,
        onChange: handleEvent,
        onBlur: handleEvent
    }), /*#__PURE__*/ React__default.createElement(TextArea$1, {
        name: name,
        maxLength: maxLength,
        "aria-describedby": `${resolvedId}_error ${resolvedId}_count`,
        "data-t": testId
    }));
};

const ComposedFormContext = /*#__PURE__*/ React.createContext({});
const ComposedForm = (props)=>{
    const [formState, setFormState] = React.useState(props.initialFormState);
    const state = React.useMemo(()=>({
            formName: props.name,
            formState: formState || {},
            formValid: !!formState && isValidComposedState(formState)
        })
    , [
        props.name,
        formState
    ]);
    // This effect calls onChange whenever `state` changes
    React.useEffect(()=>props.onChange && props.onChange(state)
    , [
        state
    ]);
    // This effect resets state when `initialFormState` changes
    // NOTE: this uses useLayoutEffect to work around a race condition with DateOfBirthSelect's initial onChange
    React.useLayoutEffect(()=>setFormState(props.initialFormState)
    , [
        props.initialFormState
    ]);
    // These callbacks are used by form Parts to get and update their part of `formState`
    const composedContext = {
        formName: props.name,
        onChange: React.useCallback((name, controlState)=>{
            setFormState((current)=>swcHelpers.objectSpread({}, current, {
                    [name]: swcHelpers.objectSpread({}, current && current[name], {
                        [controlState.name]: controlState
                    })
                })
            );
        }, []),
        getInitialState: React.useCallback((name)=>formState && formState[name]
        , [
            formState
        ])
    };
    // The native form's onSubmit is intercepted and the current `state` added
    const handleFormSubmit = React.useCallback((ev)=>{
        ev && ev.preventDefault() // this behavior is to remain consistent with Form
        ;
        props.onSubmit && props.onSubmit(state);
    }, [
        props.name,
        props.onSubmit,
        state
    ]);
    return /*#__PURE__*/ React.createElement(ComposedFormContext.Provider, {
        value: composedContext
    }, /*#__PURE__*/ React.createElement("form", {
        noValidate: true,
        id: props.name,
        className: props.className,
        "data-t": props['data-t'] || props.name,
        onSubmit: handleFormSubmit,
        children: props.children
    }));
};
const Part = ({ name , children  })=>{
    const { formName , onChange , getInitialState  } = React.useContext(ComposedFormContext);
    !onChange && console.error('ComposedForm.Part cannot be used outside of a ComposedForm');
    // These callbacks for the regular FormContext act as a proxy to add this
    // Part's `name` to get and update the correct part of ComposedForm's state
    const formContext = {
        name: formName,
        onChange: React.useCallback((state)=>onChange && onChange(name, state)
        , [
            name,
            onChange
        ]),
        getInitialState: React.useCallback((fieldName)=>(getInitialState && getInitialState(name) || {})[fieldName]
        , [
            getInitialState
        ])
    };
    return /*#__PURE__*/ React.createElement(FormContext.Provider, {
        value: formContext,
        children: children
    });
};
ComposedForm.Part = Part;

class RadioButtons extends (React.Component) {
    componentDidUpdate(prevProps) {
        const previousValue = prevProps.initialValue && prevProps.initialValue;
        const value = this.props.initialValue;
        if (value !== previousValue) {
            this.setState({
                value
            });
        }
    }
    componentDidMount() {
        this.props.onChange && this.props.onChange(this.state);
    }
    render() {
        const _props = this.props, { items , scope , alignment , className , formName  } = _props, rest = swcHelpers.objectWithoutProperties(_props, [
            "items",
            "scope",
            "alignment",
            "className",
            "formName"
        ]);
        const directionClass = alignment === 'horizontal' ? 'radio-buttons--horizontal' : 'radio-buttons--vertical';
        return /*#__PURE__*/ React.createElement("fieldset", {
            "data-t": rest['data-t'],
            className: classnames('radio-buttons', directionClass, className)
        }, items && items.length > 0 && items.map((item, i)=>{
            const inputId = `${formName || 'radio-buttons'}_${item.value.toString().replace(/\s/g, '-') || i}`;
            return /*#__PURE__*/ React.createElement(RadioButton, {
                key: item.value,
                id: inputId,
                name: scope,
                variant: "plain",
                value: String(item.value),
                isChecked: item.value === this.state.value,
                "aria-checked": item.value === this.state.value,
                disabled: item.disabled,
                onChange: this.handleChange
            }, item.label);
        }));
    }
    constructor(...args){
        super(...args);
        this.state = {
            name: this.props.scope,
            value: this.props.initialValue,
            required: !!this.props.initialValue === false,
            isTouched: false
        };
        this.handleChange = (event)=>{
            const value = event.currentTarget.value;
            const updatedState = swcHelpers.objectSpread({}, this.state, {
                value
            });
            this.setState(updatedState);
            this.props.onChange && this.props.onChange(updatedState);
        };
    }
}
RadioButtons.defaultProps = {
    items: [],
    alignment: 'horizontal',
    formName: ''
};
const ConnectedRadioButtons = (_param)=> {
    var { onChange , scope  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "onChange",
        "scope"
    ]);
    return React.createElement(FormContext.Consumer, null, (context = {})=>{
        let value = rest.initialValue || '';
        if (context.getInitialState && scope) {
            const fieldState = context.getInitialState(scope);
            if (fieldState) {
                value = fieldState.value;
            }
        }
        return /*#__PURE__*/ React.createElement(RadioButtons, swcHelpers.extends({}, rest, {
            scope: scope,
            formName: context.name,
            onChange: context.onChange || onChange,
            initialValue: value || rest.initialValue && rest.initialValue
        }));
    });
};
ConnectedRadioButtons.defaultProps = swcHelpers.objectSpread({}, RadioButtons.defaultProps);

// Value is empty because that way :invalid gets applied to the select when the placeholder is selected
const placeholderValue = '';
class Select extends React.Component {
    shouldComponentUpdate(nextProps, nextState) {
        return Object.entries(this.props).reduce((shouldUpdate, [propKey, currentPropValue])=>{
            const nextPropValue = nextProps[propKey];
            if (propKey === 'initialState') {
                const keys = Object.keys(nextProps.initialState || {});
                return keys.reduce((update, k)=>{
                    var ref, ref1;
                    return update || ((ref = nextProps.initialState) === null || ref === void 0 ? void 0 : ref[k]) === ((ref1 = this.props.initialState) === null || ref1 === void 0 ? void 0 : ref1[k]);
                }, true);
            } else {
                return shouldUpdate || nextPropValue === currentPropValue;
            }
        }, true);
    }
    componentDidUpdate(prevProps) {
        if (prevProps.initialState !== this.props.initialState) {
            this.setState({
                selectedOptionKey: this.props.initialState && this.props.initialState.value,
                isTouched: this.props.initialState && this.props.initialState.isTouched
            });
        }
    }
    getOptions() {
        return this.props.sortOptionsBy ? Object.keys(this.props.options).sort(this.props.sortOptionsBy) : Object.keys(this.props.options);
    }
    render() {
        const _props = this.props, { id , name , label , className , placeholder , iconName , disabled , required =true , hideAsteriskSign =false , 'data-t': testId  } = _props, rest = swcHelpers.objectWithoutProperties(_props, [
            "id",
            "name",
            "label",
            "className",
            "placeholder",
            "iconName",
            "disabled",
            "required",
            "hideAsteriskSign",
            'data-t'
        ]);
        var _selectedOptionKey;
        return /*#__PURE__*/ React.createElement("div", {
            className: classnames('select-field', className, {
                'select-field--disabled': disabled
            })
        }, label && /*#__PURE__*/ React.createElement("label", {
            className: "select-field__label",
            htmlFor: id
        }, label, required && !hideAsteriskSign && /*#__PURE__*/ React.createElement("span", {
            className: "select-field__asterisk"
        }, "*")), /*#__PURE__*/ React.createElement("div", {
            className: "select-field__select-wrapper"
        }, /*#__PURE__*/ React.createElement("select", {
            id: id,
            name: name,
            className: "select-field__select",
            "data-t": testId,
            onChange: this.onChange,
            onFocus: this.onFocus,
            value: (_selectedOptionKey = this.state.selectedOptionKey) !== null && _selectedOptionKey !== void 0 ? _selectedOptionKey : placeholderValue,
            disabled: disabled,
            required: !!placeholder,
            "aria-labelledby": rest['aria-labelledby'],
            "aria-describedby": rest['aria-describedby'],
            "aria-invalid": rest['aria-invalid']
        }, placeholder && /*#__PURE__*/ React.createElement("option", {
            "data-t": "select-placeholder",
            "aria-selected": "false",
            disabled: required,
            value: placeholderValue
        }, placeholder), this.getOptions().map((key)=>/*#__PURE__*/ React.createElement("option", {
                value: key,
                key: key,
                "aria-selected": this.state.selectedOptionKey === key
            }, key)
        )), /*#__PURE__*/ React.createElement(Icon, {
            name: iconName || 'chevron-down',
            className: "select-field__icon",
            "data-t": "select-field-icon"
        })));
    }
    constructor(...args){
        super(...args);
        this.state = {
            selectedOptionKey: this.props.initialState && this.props.initialState.value,
            isTouched: this.props.isTouched || false
        };
        this.onChange = (event)=>{
            const key = event.currentTarget.value;
            const value = this.props.options[key];
            this.props.onChange && this.props.onChange(value);
            this.setState({
                selectedOptionKey: key,
                isTouched: true
            });
        };
        this.onFocus = ()=>{
            if (this.props.selectionStart && !this.state.isTouched && !(this.props.initialState && this.props.initialState.value)) {
                this.props.onChange && this.props.onChange(this.props.options[this.props.selectionStart]);
                this.setState({
                    selectedOptionKey: this.props.selectionStart
                });
            }
        };
    }
}

const FormGroup = ({ columns =1 , children , className  })=>/*#__PURE__*/ React.createElement("div", {
        className: classnames('form-group', {
            'form-group--half': columns === 2,
            'form-group--third': columns === 3
        }, className)
    }, children)
;

const createOptions = (min, max)=>{
    let options = {};
    for(let i = min; i <= max; i++){
        const value = i.toString();
        options = swcHelpers.objectSpread({}, options, {
            [value]: value
        });
    }
    return options;
};
const reducer = (state, action)=>{
    const { day , month , year , isTouched  } = state;
    switch(action.type){
        case 'day':
            return {
                day: action.payload,
                month,
                year,
                isTouched: isTouched || !!month && !!year
            };
        case 'month':
            return {
                day,
                month: action.payload,
                year,
                isTouched: isTouched || !!day && !!year
            };
        case 'year':
            return {
                day,
                month,
                year: action.payload,
                isTouched: isTouched || !!day && !!month
            };
        case 'reset':
            return initialize(action.payload);
        default:
            console.warn(`Unknown action: ${action}`);
            return state;
    }
};
// If we get the day back from the details call, it might be in a Date format, which splits the day into a day + time and zone, we want to trim it to just the day if this is the case (checkout)
const formatDayFromDetailsCall = (day)=>{
    return (day === null || day === void 0 ? void 0 : day.length) > 2 ? day.split('T')[0] : day;
};
const initialize = (initialState)=>{
    if (initialState) {
        const [year, month, day] = initialState.value.split('-');
        return {
            day: formatDayFromDetailsCall(day) || '',
            month: month || '',
            year: year || '',
            isTouched: initialState.isTouched || false
        };
    }
    return {
        day: '',
        month: '',
        year: '',
        isTouched: false
    };
};
const DateOfBirthSelect = ({ dateFormat =DateFormat.dmy , minYear , maxYear , dayPlaceholder , monthPlaceholder , yearPlaceholder , errorMessage , label , tooltipLabel , onChange , initialState , validations , required =false , hideAsteriskSign =false , name ='dateOfBirth' ,  })=>{
    const [state, dispatch] = useReducer(reducer, initialState, initialize);
    const { day , month , year , isTouched  } = state;
    const readonly = initialState && initialState.readonly;
    const labelId = `${label}-${name}`;
    const value = useMemo(()=>year && month && day ? [
            year,
            month,
            day
        ].join('-') : ''
    , [
        state
    ]);
    const isValid = useMemo(()=>!validations || FormControl.validate(validations, required, value)
    , [
        validations,
        value
    ]);
    useEffect(()=>{
        dispatch({
            type: 'reset',
            payload: initialState
        });
    }, [
        initialState && initialState.value,
        initialState && initialState.isTouched
    ]);
    useEffect(()=>{
        onChange && onChange(swcHelpers.objectSpread({
            name,
            value,
            isValid,
            isTouched
        }, readonly === undefined ? {} : {
            readonly
        }));
    }, [
        onChange,
        name,
        value,
        isValid,
        isTouched
    ]);
    const classes = classnames({
        _is_valid: isValid && isTouched && !readonly,
        _is_invalid: !isValid && isTouched && !readonly
    });
    const handleDayChange = React.useCallback((payload)=>{
        dispatch({
            type: 'day',
            payload
        });
    }, []);
    const handleMonthChange = React.useCallback((payload)=>{
        dispatch({
            type: 'month',
            payload
        });
    }, []);
    const handleYearChange = React.useCallback((payload)=>{
        dispatch({
            type: 'year',
            payload
        });
    }, []);
    // Convert dateFormat string to array
    const format = [
        ...dateFormat
    ];
    const selectionStart = React.useMemo(()=>{
        if (maxYear) {
            return String(maxYear);
        }
        if (minYear) {
            return String(minYear);
        }
        return String(new Date().getFullYear() - 18);
    }, [
        minYear,
        maxYear
    ]);
    const selectInputs = {
        d: React.useMemo(()=>/*#__PURE__*/ React.createElement(Select, {
                className: classes,
                id: `day-${name}`,
                name: "day",
                key: `day-${name}`,
                placeholder: dayPlaceholder,
                options: createOptions(DATES_RANGE.DAY.min, DATES_RANGE.DAY.max),
                onChange: handleDayChange,
                // Remove leading 0, the BE might send the number back as 01, we want to make sure the intial state shows correctly
                initialState: {
                    value: day.replace(/^0+/, '')
                },
                disabled: readonly,
                "aria-labelledby": labelId
            })
        , [
            classes,
            name,
            dayPlaceholder,
            createOptions,
            DATES_RANGE.DAY.min,
            DATES_RANGE.DAY.max,
            day,
            readonly, 
        ]),
        m: React.useMemo(()=>/*#__PURE__*/ React.createElement(Select, {
                className: classes,
                id: `month-${name}`,
                name: "month",
                key: `month-${name}`,
                placeholder: monthPlaceholder,
                options: createOptions(DATES_RANGE.MONTH.min, DATES_RANGE.MONTH.max),
                onChange: handleMonthChange,
                // Remove leading 0, the BE might send the number back as 01, we want to make sure the intial state shows correctly
                initialState: {
                    value: month.replace(/^0+/, '')
                },
                disabled: readonly,
                "aria-labelledby": labelId
            })
        , [
            classes,
            name,
            monthPlaceholder,
            createOptions,
            DATES_RANGE.MONTH.min,
            DATES_RANGE.MONTH.max,
            month,
            readonly, 
        ]),
        y: React.useMemo(()=>/*#__PURE__*/ React.createElement(Select, {
                className: classes,
                id: `year-${name}`,
                name: "year",
                key: `year-${name}`,
                placeholder: yearPlaceholder,
                options: createOptions(minYear !== null && minYear !== void 0 ? minYear : DATES_RANGE.YEAR_OF_BIRTH.min, maxYear !== null && maxYear !== void 0 ? maxYear : DATES_RANGE.YEAR_OF_BIRTH.max),
                onChange: handleYearChange,
                initialState: {
                    value: year
                },
                disabled: readonly,
                selectionStart: selectionStart,
                sortOptionsBy: (a, b)=>b.localeCompare(a, 'en', {
                        sensitivity: 'base',
                        usage: 'sort'
                    })
                ,
                "aria-labelledby": labelId
            })
        , [
            classes,
            name,
            yearPlaceholder,
            createOptions,
            minYear,
            maxYear,
            DATES_RANGE.YEAR_OF_BIRTH.min,
            DATES_RANGE.YEAR_OF_BIRTH.max,
            year,
            readonly, 
        ])
    };
    return /*#__PURE__*/ React.createElement("div", {
        className: "form-control date-of-birth-select"
    }, label && /*#__PURE__*/ React.createElement("div", {
        className: "form-control__label"
    }, /*#__PURE__*/ React.createElement("span", {
        id: labelId
    }, label), required && !hideAsteriskSign && /*#__PURE__*/ React.createElement("span", {
        className: "form-control__asterisk"
    }, "*"), tooltipLabel && /*#__PURE__*/ React.createElement(Tooltip, {
        id: `${name}-tooltip`,
        message: tooltipLabel
    })), /*#__PURE__*/ React.createElement("div", {
        className: "date-of-birth-select__container"
    }, format.map((entry)=>selectInputs[entry]
    )), !isValid && isTouched && /*#__PURE__*/ React.createElement("div", {
        className: "form-control__error"
    }, errorMessage));
};
const MemoizedDateOfBirthSelect = /*#__PURE__*/ React.memo(DateOfBirthSelect);
MemoizedDateOfBirthSelect.displayName = 'DateOfBirthSelect';
const ConnectedDateOfBirthSelect = (props)=>/*#__PURE__*/ React.createElement(FormContext.Consumer, null, (context = {})=>/*#__PURE__*/ React.createElement(MemoizedDateOfBirthSelect, swcHelpers.extends({}, props, {
            onChange: context.onChange || props.onChange,
            initialState: context.getInitialState && context.getInitialState(props.name || 'dateOfBirth') || props.initialState
        }))
    )
;
ConnectedDateOfBirthSelect.defaultProps = swcHelpers.objectSpread({}, DateOfBirthSelect.defaultProps);

const PrefixSelect = ({ errorMessage , label , tooltipLabel , placeholder ='Prefix' , onChange , options , initialState , required =false , hideAsteriskSign =false , name ='salutation' ,  })=>{
    const [value, setValue] = useState(initialState && initialState.value || '');
    const [isTouched, setIsTouched] = useState(initialState && initialState.isTouched || false);
    const isValid = useMemo(()=>FormControl.validate([], required, value)
    , [
        value,
        required
    ]);
    const readonly = initialState && initialState.readonly;
    React.useEffect(()=>{
        onChange && onChange(swcHelpers.objectSpread({
            value,
            name,
            isValid,
            isTouched
        }, readonly === undefined ? {} : {
            readonly
        }));
    }, [
        onChange,
        value,
        name,
        isValid,
        isTouched
    ]);
    React.useEffect(()=>{
        setValue((initialState === null || initialState === void 0 ? void 0 : initialState.value) || '');
        setIsTouched((initialState === null || initialState === void 0 ? void 0 : initialState.isTouched) || false);
    }, [
        initialState === null || initialState === void 0 ? void 0 : initialState.value,
        initialState === null || initialState === void 0 ? void 0 : initialState.isTouched
    ]);
    const handleChange = useCallback((option)=>{
        setValue(option.label);
        setIsTouched(true);
    }, [
        required
    ]);
    const classes = classnames('prefix-select__input', {
        _is_valid: isValid && isTouched && !readonly,
        _is_invalid: !isValid && isTouched && !readonly
    });
    return /*#__PURE__*/ React.createElement("label", {
        className: "form-control prefix-select"
    }, label && /*#__PURE__*/ React.createElement("div", {
        className: "form-control__label"
    }, label, required && !hideAsteriskSign && /*#__PURE__*/ React.createElement("span", {
        className: "form-control__asterisk"
    }, "*"), tooltipLabel && /*#__PURE__*/ React.createElement(Tooltip, {
        id: `${name}-tooltip`,
        message: tooltipLabel
    })), /*#__PURE__*/ React.createElement("div", {
        className: "form-control__wrapper"
    }, /*#__PURE__*/ React.createElement(Select, {
        className: classes,
        id: "salutation",
        name: "salutation",
        placeholder: placeholder,
        options: options,
        initialState: {
            value,
            isTouched,
            isValid
        },
        onChange: handleChange,
        disabled: readonly
    })), !isValid && isTouched && /*#__PURE__*/ React.createElement("span", {
        className: "form-control__error"
    }, errorMessage));
};
const ConnectedPrefixSelect = (props)=>/*#__PURE__*/ React.createElement(FormContext.Consumer, null, (context = {})=>/*#__PURE__*/ React.createElement(PrefixSelect, swcHelpers.extends({}, props, {
            onChange: context.onChange,
            initialState: context.getInitialState && context.getInitialState(props.name || 'salutation') || props.initialState
        }))
    )
;
ConnectedPrefixSelect.defaultProps = swcHelpers.objectSpread({}, PrefixSelect.defaultProps);

const MultipleAddressLine = ({ addLineLabel , errorMessage , getInitialState , initialLineAmount =1 , label , lineThreeLabel , lineTwoLabel , maxLength , required =true , shouldCombineAddress =false , streetComponent: Street = ConnectedFormControl , validations  })=>{
    const [lineAmount, setLineAmount] = useState(initialLineAmount);
    const additionalStreetInfoValue = getInitialState && getInitialState('additionalStreetInfo');
    const additionalAddressInfoValue = getInitialState && getInitialState('additionalAddressInfo');
    if (additionalStreetInfoValue && additionalStreetInfoValue.value && lineAmount < 2) {
        setLineAmount(2);
    }
    if (additionalAddressInfoValue && additionalAddressInfoValue.value && lineAmount < 3) {
        setLineAmount(3);
    }
    const handleClick = ()=>{
        setLineAmount(lineAmount + 1);
    };
    const inputs = [
        /*#__PURE__*/ React__default.createElement(Street, swcHelpers.extends({}, ConnectedFormControl.defaultProps, {
            id: "streetName",
            key: "streetName",
            "data-t": "streetName",
            label: label,
            initialState: getInitialState && getInitialState('streetName'),
            errorMessage: errorMessage,
            validations: validations,
            required: required,
            maxLength: maxLength,
            shouldCombineAddress: shouldCombineAddress
        })),
        /*#__PURE__*/ React__default.createElement("div", {
            key: "additionalStreetInfo",
            "data-t": "additionalStreetInfo",
            className: classnames('multiple-address-line__additional-line', {
                'multiple-address-line__additional-line--hidden': lineAmount < 2
            })
        }, /*#__PURE__*/ React__default.createElement(ConnectedFormControl, swcHelpers.extends({}, ConnectedFormControl.defaultProps, {
            id: "additionalStreetInfo",
            key: "additionalStreetInfo",
            "data-t": "additionalStreetInfoFormControl",
            required: false,
            initialState: getInitialState && getInitialState('additionalStreetInfo'),
            errorMessage: errorMessage,
            validations: validations,
            maxLength: maxLength,
            label: lineTwoLabel
        }))),
        /*#__PURE__*/ React__default.createElement("div", {
            key: "additionalAddressInfo",
            "data-t": "additionalAddressInfo",
            className: classnames('multiple-address-line__additional-line', {
                'multiple-address-line__additional-line--hidden': lineAmount < 3
            })
        }, /*#__PURE__*/ React__default.createElement(ConnectedFormControl, swcHelpers.extends({}, ConnectedFormControl.defaultProps, {
            key: "additionalAddressInfo",
            id: "additionalAddressInfo",
            "data-t": "additionalAddressInfoFormControl",
            required: false,
            initialState: getInitialState && getInitialState('additionalAddressInfo'),
            errorMessage: errorMessage,
            validations: validations,
            maxLength: maxLength,
            label: lineThreeLabel
        }))), 
    ];
    return /*#__PURE__*/ React__default.createElement("div", {
        className: "multiple-address-line"
    }, inputs, lineAmount < inputs.length && /*#__PURE__*/ React__default.createElement("button", {
        type: "button",
        className: "multiple-address-line__add-lines",
        onClick: handleClick
    }, addLineLabel));
};
const ConnectedMultipleAddressLine = (_param)=> {
    var props = swcHelpers.extends({}, _param);
    return React__default.createElement(FormContext.Consumer, null, (context = {})=>/*#__PURE__*/ React__default.createElement(MultipleAddressLine, swcHelpers.extends({}, props, {
            getInitialState: context.getInitialState
        }))
    );
};
ConnectedMultipleAddressLine.defaultProps = swcHelpers.objectSpread({}, MultipleAddressLine.defaultProps);

const ReactDatePicker = (_param)=>{
    var { locale , dateFnsLocale  } = _param, props = swcHelpers.objectWithoutProperties(_param, [
        "locale",
        "dateFnsLocale"
    ]);
    const [Module, setModule] = React__default.useState(null);
    const loadComponent = React__default.useCallback(swcHelpers.asyncToGenerator(function*() {
        const importedModule = yield import(/* webpackChunkName: 'react-datepicker' */ 'react-datepicker');
        const { registerLocale  } = importedModule;
        registerLocale(locale, dateFnsLocale);
        setModule(importedModule);
    }), [
        locale,
        dateFnsLocale
    ]);
    React__default.useEffect(()=>{
        loadComponent();
    }, [
        loadComponent
    ]);
    // Don't render if we didn't register the (date-fns)locale correctly
    // as we're not able to translate days/months without it
    return Module && /*#__PURE__*/ React__default.createElement(Module.default, swcHelpers.extends({
        locale: locale
    }, props));
};

const createCustomHeader = (dateFnsLocale)=>{
    return function({ date , decreaseMonth , increaseMonth , prevMonthButtonDisabled , nextMonthButtonDisabled  }) {
        var ref;
        return /*#__PURE__*/ React__default.createElement("div", {
            className: "react-datepicker__custom-nav"
        }, /*#__PURE__*/ React__default.createElement("button", {
            className: "react-datepicker__navigation react-datepicker__navigation--previous",
            onClick: decreaseMonth,
            disabled: prevMonthButtonDisabled,
            type: "button"
        }, /*#__PURE__*/ React__default.createElement(Icon, {
            name: "chevron-left"
        })), /*#__PURE__*/ React__default.createElement("div", {
            className: "react-datepicker__current-month"
        }, (dateFnsLocale === null || dateFnsLocale === void 0 ? void 0 : (ref = dateFnsLocale.localize) === null || ref === void 0 ? void 0 : ref.month(getMonth(date))) || getMonth(date), " ", getYear(date)), /*#__PURE__*/ React__default.createElement("button", {
            className: "react-datepicker__navigation react-datepicker__navigation--next",
            onClick: increaseMonth,
            disabled: nextMonthButtonDisabled,
            type: "button"
        }, /*#__PURE__*/ React__default.createElement(Icon, {
            name: "chevron-right"
        })));
    };
};
const DatePicker = ({ onChange , onBlur , className , label , tooltip , placeholder , optionalText , errorMessage , initialValue , minDate , maxDate , dateFormat ='MMMM d, yyyy' , disabled =false , required =true , hideAsteriskSign =false , useNativeInput =false , 'data-t': testId = 'date-picker' ,  })=>{
    const { getLabel , locale , dateFnsLocale  } = useIntl();
    const [date1, setDate] = React__default.useState(initialValue);
    const [isValid$1, setIsValid] = React__default.useState(true);
    const [hasFocus, setHasFocus] = React__default.useState(false);
    const [isTouched, setIsTouched] = React__default.useState(false);
    const customHeader = React__default.useMemo(()=>createCustomHeader(dateFnsLocale)
    , [
        dateFnsLocale
    ]);
    const handleBlur = React__default.useCallback(()=>{
        setHasFocus(false);
        onBlur && onBlur();
    }, [
        onBlur
    ]);
    const handleFocus = React__default.useCallback(()=>{
        if (!disabled) {
            setHasFocus(true);
        }
    }, [
        disabled
    ]);
    React__default.useCallback(()=>!disabled && setHasFocus((current)=>!current
        )
    , [
        disabled, 
    ]);
    const handleChange = React__default.useCallback((date)=>{
        setDate(date || undefined);
        !isTouched && setIsTouched(true);
    }, [
        isTouched
    ]);
    const onNativeChange = React__default.useCallback((e)=>{
        if (!e.target.value) {
            /*
         * Clearing the input is not supported by the current interfaces,
         * fallback to the current value. This shouldn't happen since
         * we hide the clear button with css as well.
         */ e.target.value = date1 && DateUtils.toLocalDate(date1);
            return;
        }
        handleChange(new Date(e.target.value));
    }, [
        handleChange
    ]);
    React__default.useEffect(()=>{
        if (date1 && isValid(date1)) {
            onChange(date1);
            setHasFocus(false);
            isTouched && setIsValid(true);
        } else if (required) {
            isTouched && setIsValid(false);
        }
    }, [
        date1,
        onChange,
        required,
        isTouched
    ]);
    return /*#__PURE__*/ React__default.createElement("div", {
        className: classnames('form-control date-picker', className, {
            'date-picker--focus': hasFocus,
            'date-picker--disabled': disabled
        }),
        "data-t": testId
    }, label && /*#__PURE__*/ React__default.createElement("label", {
        className: "form-control__label"
    }, label, required && !hideAsteriskSign && /*#__PURE__*/ React__default.createElement("span", {
        className: "form-control__asterisk"
    }, "*"), tooltip && /*#__PURE__*/ React__default.createElement(Tooltip, {
        message: tooltip,
        id: "date-picker-tooltip"
    })), !required && optionalText && /*#__PURE__*/ React__default.createElement("small", {
        className: "form-control__optional-tag"
    }, optionalText), /*#__PURE__*/ React__default.createElement("div", {
        className: "date-picker__input-container"
    }, /*#__PURE__*/ React__default.createElement(Icon, {
        name: "calendar"
    }), useNativeInput ? /*#__PURE__*/ React__default.createElement("input", {
        type: "date",
        className: classnames('form-control__input', 'date-picker__input', 'date-picker__input--native', {
            _is_invalid: !isValid$1
        }),
        value: date1 && DateUtils.toLocalDate(date1),
        min: minDate && DateUtils.toLocalDate(minDate),
        max: maxDate && DateUtils.toLocalDate(maxDate),
        onClick: handleFocus,
        onChange: onNativeChange,
        onBlur: handleBlur,
        disabled: disabled,
        required: required,
        "aria-required": required,
        "aria-label": getLabel('common.selectDate')
    }) : dateFnsLocale && /*#__PURE__*/ React__default.createElement(ReactDatePicker, {
        dateFnsLocale: dateFnsLocale,
        className: classnames('form-control__input', 'date-picker__input', {
            _is_invalid: !isValid$1
        }),
        selected: date1,
        onFocus: handleFocus,
        onChange: handleChange,
        onBlur: handleBlur,
        placeholderText: placeholder,
        dateFormat: dateFormat,
        locale: locale,
        minDate: minDate,
        maxDate: maxDate,
        disabled: disabled,
        renderCustomHeader: customHeader,
        customInput: /*#__PURE__*/ React__default.createElement("input", {
            placeholder: placeholder,
            "aria-label": getLabel('common.selectDate')
        })
    }), /*#__PURE__*/ React__default.createElement(Icon, {
        name: "chevron-down"
    })), errorMessage && !isValid$1 && /*#__PURE__*/ React__default.createElement("span", {
        className: "form-control__error"
    }, errorMessage));
};

const RangeDatePicker = (_param)=>{
    var { labelStart , labelEnd , tooltip , onSelectStartDate , onSelectEndDate , startDate , endDate , startDatePlaceholder , endDatePlaceholder , required =true , dateFormat ='MMMM d, yyyy' , maxRangeWeeks =2 , hideAsteriskSign =false , useNativeInput =false , today =new Date() , startBoundaries , endBoundaries , className  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "labelStart",
        "labelEnd",
        "tooltip",
        "onSelectStartDate",
        "onSelectEndDate",
        "startDate",
        "endDate",
        "startDatePlaceholder",
        "endDatePlaceholder",
        "required",
        "dateFormat",
        "maxRangeWeeks",
        "hideAsteriskSign",
        "useNativeInput",
        "today",
        "startBoundaries",
        "endBoundaries",
        "className"
    ]);
    var ref, ref1, ref2, ref3;
    return /*#__PURE__*/ React__default.createElement("div", swcHelpers.extends({}, rest, {
        className: classnames('range-date-picker', {
            'range-date-picker--with-end-label': Boolean(labelEnd)
        }, className)
    }), /*#__PURE__*/ React__default.createElement(DatePicker, {
        key: `start-${Date.now()}`,
        className: "date-picker--start",
        label: labelStart,
        tooltip: tooltip,
        required: required,
        onChange: onSelectStartDate,
        placeholder: startDatePlaceholder,
        dateFormat: dateFormat,
        initialValue: startDate,
        minDate: (ref = startBoundaries === null || startBoundaries === void 0 ? void 0 : startBoundaries.minDate) !== null && ref !== void 0 ? ref : today,
        maxDate: (ref1 = startBoundaries === null || startBoundaries === void 0 ? void 0 : startBoundaries.maxDate) !== null && ref1 !== void 0 ? ref1 : DateUtils.shiftDate(today, 'week', maxRangeWeeks),
        hideAsteriskSign: hideAsteriskSign,
        useNativeInput: useNativeInput
    }), /*#__PURE__*/ React__default.createElement(DatePicker, {
        key: `end-${Date.now()}`,
        className: "date-picker--end",
        label: labelEnd,
        onChange: onSelectEndDate,
        required: required,
        placeholder: endDatePlaceholder,
        initialValue: endDate,
        minDate: (ref2 = endBoundaries === null || endBoundaries === void 0 ? void 0 : endBoundaries.minDate) !== null && ref2 !== void 0 ? ref2 : today,
        maxDate: (ref3 = endBoundaries === null || endBoundaries === void 0 ? void 0 : endBoundaries.maxDate) !== null && ref3 !== void 0 ? ref3 : DateUtils.shiftDate(today, 'week', maxRangeWeeks),
        dateFormat: dateFormat,
        hideAsteriskSign: hideAsteriskSign,
        useNativeInput: useNativeInput
    }));
};

const RequiredFieldsLabel = ({ className  })=>{
    const { getLabel  } = useIntl();
    return /*#__PURE__*/ React.createElement("div", {
        className: classnames('required-fields', className)
    }, /*#__PURE__*/ React.createElement("span", {
        className: "required-fields__asterisk"
    }, "*"), /*#__PURE__*/ React.createElement("div", {
        className: "required-fields__label"
    }, getLabel('common.form.field.required')));
};

const CheckboxView = (_param)=>{
    var { id , className , label , checked , disabled , error , required  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "id",
        "className",
        "label",
        "checked",
        "disabled",
        "error",
        "required"
    ]);
    const inputRef = useRef(null);
    const onClick = (e)=>{
        rest.onChange && rest.onChange({
            value: rest === null || rest === void 0 ? void 0 : rest.value,
            checked: e.currentTarget.checked
        });
    };
    React__default.useEffect(()=>{
        if (inputRef.current) {
            inputRef.current.indeterminate = checked === 'mixed';
        }
    }, [
        checked,
        inputRef
    ]);
    return /*#__PURE__*/ React__default.createElement("label", {
        className: classnames('checkbox-view', {
            'checkbox-view__error': error && !checked
        }, className)
    }, /*#__PURE__*/ React__default.createElement("input", {
        className: "checkbox-view__input",
        type: "checkbox",
        id: id,
        "aria-checked": checked,
        "aria-label": rest['aria-label'],
        "aria-labelledby": rest['aria-label'] || !id ? undefined : `label_${id}`,
        "aria-invalid": error && !checked,
        "aria-describedby": error && !checked ? (rest === null || rest === void 0 ? void 0 : rest.errorId) || `error_${id}` : undefined,
        checked: typeof checked === 'boolean' ? checked : false,
        disabled: disabled,
        onChange: disabled ? undefined : onClick,
        "data-t": rest['data-t'],
        tabIndex: 0,
        ref: inputRef,
        value: "0" // Not really used anywhere but it's a required attribute
        ,
        required: required
    }), /*#__PURE__*/ React__default.createElement("span", {
        className: "checkbox-view__mask",
        "aria-hidden": true
    }), checked === 'mixed' ? /*#__PURE__*/ React__default.createElement("span", {
        className: "checkbox-view__indeterminate",
        "aria-hidden": true
    }) : /*#__PURE__*/ React__default.createElement("svg", {
        className: "checkbox-view__checkmark",
        width: "14",
        height: "13",
        viewBox: "0 0 14 13",
        fill: "none",
        xmlns: "http://www.w3.org/2000/svg",
        "aria-hidden": true
    }, /*#__PURE__*/ React__default.createElement("path", {
        d: "M1 7.5L4.5 12L13 1",
        fill: "none"
    })), /*#__PURE__*/ React__default.createElement("span", {
        id: id && `label_${id}`,
        className: "checkbox-view__label"
    }, label));
};
var CheckboxView$1 = /*#__PURE__*/ memo(CheckboxView);

const ErrorMessage = ({ error , id , errorMessage  })=>{
    return error && !!errorMessage ? /*#__PURE__*/ React__default.createElement("div", {
        id: id,
        className: "error-message__error"
    }, /*#__PURE__*/ React__default.createElement(Icon, {
        className: "error-message__icon",
        key: "alert-circle",
        name: "alert-circle",
        height: 16,
        width: 16
    }), errorMessage) : null;
};
var ErrorMessage$1 = /*#__PURE__*/ memo(ErrorMessage);

/**
 *  A Checkbox component with Label and Error message support
 *
 *  It should be used as replacement for elements/Checkbox component
 *
 *  @Props : CheckboxViewProps, ErrorProps
 */ const CheckboxWithLabel = (_param)=>{
    var { className  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "className"
    ]);
    var ref;
    const errorIdFromMsg = rest.errorMessage ? `error_${(ref = rest.errorMessage) === null || ref === void 0 ? void 0 : ref.replace(/[^A-Z0-9]/gi, '_')}` : undefined;
    const errorId = (rest.id ? `error_${rest.id}` : undefined) || errorIdFromMsg;
    return /*#__PURE__*/ React.createElement("div", {
        className: classnames('checkbox-with-label', className)
    }, /*#__PURE__*/ React.createElement(CheckboxView$1, swcHelpers.extends({}, rest, {
        errorId: errorId
    })), /*#__PURE__*/ React.createElement(ErrorMessage$1, {
        id: errorId || '',
        error: rest.error,
        errorMessage: rest.errorMessage
    }));
};

const RecaptchaContext = /*#__PURE__*/ React__default.createContext({});
const RecaptchaProvider = ({ children , settings  })=>{
    const context = {
        settings
    };
    return /*#__PURE__*/ React__default.createElement(RecaptchaContext.Provider, {
        value: context
    }, children);
};

const useRecaptchaContext = ()=>React__default.useContext(RecaptchaContext)
;
const RECAPTCHA_SCRIPT_SRC = 'https://www.google.com/recaptcha/api.js';
const useRecaptcha = (options)=>{
    const { settings  } = useRecaptchaContext();
    const countRef = useRef(0);
    const TRY_INTERVAL = 80;
    const MAX_TRY_COUNT = 25;
    React__default.useEffect(()=>{
        if (!settings) {
            return;
        }
        const script = createScriptTag({
            async: true,
            src: `${RECAPTCHA_SCRIPT_SRC}?render=${settings.siteKey}`
        });
        injectTags([
            script
        ]);
    }, [
        settings
    ]);
    function checkScript(cb) {
        // check if script is being loaded
        if (typeof window.grecaptcha === 'undefined') {
            countRef.current += 1;
            if (countRef.current > MAX_TRY_COUNT) {
                return cb(new Error('Recaptcha not correctly initialised.'));
            }
            return setTimeout(()=>{
                checkScript(cb);
            }, TRY_INTERVAL);
        }
        cb(null);
    }
    const getToken = React__default.useCallback(()=>{
        return new Promise((resolve, reject)=>{
            checkScript((error)=>{
                if (error) {
                    return reject(error);
                }
                window.grecaptcha.ready(()=>{
                    const action = (options === null || options === void 0 ? void 0 : options.action) || 'form';
                    window.grecaptcha.execute(settings.siteKey, {
                        action
                    }).then(resolve).catch(reject);
                });
            });
        });
    }, []);
    return {
        getToken
    };
};

const AddressLine = (_param)=> {
    var { count ='1' , label , errorMessage , validations =[
        isAddressLine
    ]  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "count",
        "label",
        "errorMessage",
        "validations"
    ]);
    return React.createElement(ConnectedFormControl, swcHelpers.extends({}, ConnectedFormControl.defaultProps, {
        id: `addressLine${count}`,
        "data-t": `address-line-${count}`,
        label: label,
        errorMessage: errorMessage,
        validations: validations
    }, rest));
};

const City = (_param)=> {
    var { label , errorMessage , validations =[
        isText
    ]  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "label",
        "errorMessage",
        "validations"
    ]);
    return React.createElement(ConnectedFormControl, swcHelpers.extends({}, ConnectedFormControl.defaultProps, {
        id: "city",
        "data-t": "city",
        type: "text",
        label: label,
        errorMessage: errorMessage,
        validations: validations
    }, rest));
};

const ContactMethod = (_param)=> {
    var { name , className , labels: { label , tooltip , error  } , options , value , isTouched , isValid , required =false , hideAsteriskSign =false , onChange  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "name",
        "className",
        "labels",
        "options",
        "value",
        "isTouched",
        "isValid",
        "required",
        "hideAsteriskSign",
        "onChange"
    ]);
    return React.createElement("div", swcHelpers.extends({}, rest, {
        className: classnames('form-control contact-method', className)
    }), /*#__PURE__*/ React.createElement("div", {
        className: "form-control__label"
    }, label, required && !hideAsteriskSign && /*#__PURE__*/ React.createElement("span", {
        className: "form-control__asterisk"
    }, "*"), tooltip && /*#__PURE__*/ React.createElement(Tooltip, {
        id: `${name}-tooltip`,
        message: tooltip
    })), /*#__PURE__*/ React.createElement("div", {
        className: "form-control__wrapper"
    }, /*#__PURE__*/ React.createElement(ConnectedRadioButtons, {
        className: className,
        alignment: "vertical",
        scope: name,
        items: options,
        initialValue: value,
        onChange: onChange
    })), !isValid && isTouched && error && /*#__PURE__*/ React.createElement("span", {
        className: "form-control__error"
    }, error));
};

/*
 * This wrapper can only be used in conjunction with ComposedForm. If a version is needed that also
 * works with Form (and without ComposedForm), this component needs to be expanded, or duplicated
 * for that puprose.
 */ const ContactMethodInput = ({ forScope , forValues , children , forContactMethodName ='contactMethod' ,  })=>{
    var ref;
    const values = Array.isArray(forValues) ? forValues : [
        forValues
    ];
    const composedFormContext = React.useContext(ComposedFormContext);
    const { getInitialState  } = composedFormContext;
    const formState = getInitialState && getInitialState(forScope);
    const contactMethodValue = formState === null || formState === void 0 ? void 0 : (ref = formState[forContactMethodName]) === null || ref === void 0 ? void 0 : ref.value;
    const required = contactMethodValue !== undefined && values.includes(contactMethodValue);
    return children({
        required
    });
};

const NAME = 'contactMethod';
const ContactMethodContainer = (_param1)=>{
    var { className , name , labels , options , initialState , required =false , onChange  } = _param1, rest1 = swcHelpers.objectWithoutProperties(_param1, [
        "className",
        "name",
        "labels",
        "options",
        "initialState",
        "required",
        "onChange"
    ]);
    var ref;
    const [value, setValue] = React.useState((initialState === null || initialState === void 0 ? void 0 : initialState.value) || ((ref = options.find((entry)=>entry.isDefault
    )) === null || ref === void 0 ? void 0 : ref.value) || '');
    const [isTouched, setIsTouched] = React.useState((initialState === null || initialState === void 0 ? void 0 : initialState.isTouched) || false);
    const readonly = (initialState === null || initialState === void 0 ? void 0 : initialState.readonly) || false;
    const formattedOptions = React.useMemo(()=>options.map((_param)=>{
            var rest = swcHelpers.objectWithoutProperties(_param, [
                "isDefault"
            ]);
            return swcHelpers.objectSpread({}, rest, {
                disabled: readonly
            });
        })
    , [
        options,
        readonly
    ]);
    const isValid = React.useMemo(()=>{
        return FormControl.validate([], required, value);
    }, [
        required,
        value
    ]);
    const onChangeHandler = React.useCallback((formControlState)=>{
        setIsTouched(true);
        setValue(formControlState.value);
        onChange && onChange(formControlState);
    }, [
        onChange,
        setIsTouched,
        setValue
    ]);
    React.useEffect(()=>{
        setIsTouched((initialState === null || initialState === void 0 ? void 0 : initialState.isTouched) || false);
    }, [
        initialState === null || initialState === void 0 ? void 0 : initialState.isTouched
    ]);
    React.useEffect(()=>{
        if (initialState) {
            setValue(initialState.value);
        }
    }, [
        initialState === null || initialState === void 0 ? void 0 : initialState.value
    ]);
    return /*#__PURE__*/ React.createElement(ContactMethod, swcHelpers.extends({
        name: name || NAME,
        className: className,
        labels: labels,
        options: formattedOptions,
        value: value,
        isTouched: isTouched,
        isValid: isValid,
        onChange: onChangeHandler,
        required: required
    }, rest1));
};
const ConnectedContactMethodContainer = (_param)=> {
    var { name , onChange , initialState  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "name",
        "onChange",
        "initialState"
    ]);
    return React.createElement(FormContext.Consumer, null, (context = {})=>/*#__PURE__*/ React.createElement(ContactMethodContainer, swcHelpers.extends({}, rest, {
            name: name,
            onChange: context.onChange || onChange,
            initialState: context.getInitialState ? context.getInitialState(name || NAME) : initialState
        }))
    );
};
ConnectedContactMethodContainer.Input = ContactMethodInput;

const Email = (_param)=> {
    var { id ='email' , 'data-t': dataT = 'email' , label , errorMessage , validations =[
        isEmailAddress
    ]  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "id",
        'data-t',
        "label",
        "errorMessage",
        "validations"
    ]);
    return React.createElement(ConnectedFormControl, swcHelpers.extends({}, ConnectedFormControl.defaultProps, {
        id: id,
        "data-t": dataT,
        type: "email",
        label: label,
        errorMessage: errorMessage,
        validations: validations
    }, rest));
};

const FirstName = (_param)=> {
    var { label , errorMessage , validations =[
        isText
    ]  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "label",
        "errorMessage",
        "validations"
    ]);
    return React.createElement(ConnectedFormControl, swcHelpers.extends({}, ConnectedFormControl.defaultProps, {
        id: "firstName",
        "data-t": "first-name",
        label: label,
        errorMessage: errorMessage,
        validations: validations
    }, rest));
};

const MiddleName = (_param)=> {
    var { label , errorMessage , validations =[
        isText
    ]  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "label",
        "errorMessage",
        "validations"
    ]);
    return React.createElement(ConnectedFormControl, swcHelpers.extends({}, ConnectedFormControl.defaultProps, {
        id: "middleName",
        "data-t": "middle-name",
        label: label,
        errorMessage: errorMessage
    }, rest));
};

const LastName = (_param)=> {
    var { label , errorMessage , validations =[
        isText
    ]  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "label",
        "errorMessage",
        "validations"
    ]);
    return React.createElement(ConnectedFormControl, swcHelpers.extends({}, ConnectedFormControl.defaultProps, {
        id: "lastName",
        "data-t": "last-name",
        label: label,
        errorMessage: errorMessage,
        validations: validations
    }, rest));
};

const HouseNumber = (_param)=> {
    var { label , errorMessage , validations =[
        isNotDecimalNumber
    ]  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "label",
        "errorMessage",
        "validations"
    ]);
    return React.createElement(ConnectedFormControl, swcHelpers.extends({}, ConnectedFormControl.defaultProps, {
        id: "streetNumber",
        "data-t": "house-number",
        type: "text",
        label: label,
        errorMessage: errorMessage,
        validations: validations
    }, rest));
};

const HouseNumberSuffix = (_param)=> {
    var { label , errorMessage  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "label",
        "errorMessage"
    ]);
    return React.createElement(ConnectedFormControl, swcHelpers.extends({}, ConnectedFormControl.defaultProps, {
        id: "additionalStreetInfo",
        "data-t": "house-number-suffix",
        label: label,
        errorMessage: errorMessage
    }, rest));
};

const ToggleIcon = ({ onClick , className , isActive , isDisabled  })=>{
    const { getLabel  } = useIntl();
    return /*#__PURE__*/ React.createElement("button", {
        type: "button",
        "aria-label": isActive ? getLabel('account.hidePassword') : getLabel('account.showPassword'),
        onClick: onClick,
        disabled: isDisabled,
        className: className
    }, isActive ? /*#__PURE__*/ React.createElement(Icon, {
        name: "eye-slash"
    }) : /*#__PURE__*/ React.createElement(Icon, {
        name: "eye"
    }));
};
class Password extends (React.Component) {
    render() {
        const _props = this.props, { label , errorMessage  } = _props, rest = swcHelpers.objectWithoutProperties(_props, [
            "label",
            "errorMessage"
        ]);
        const { passwordVisible  } = this.state;
        return /*#__PURE__*/ React.createElement(ConnectedFormControl, swcHelpers.extends({}, ConnectedFormControl.defaultProps, {
            id: "password",
            "data-t": "password",
            "aria-describedby": rest['aria-describedby'],
            type: passwordVisible ? 'text' : 'password',
            label: label,
            className: "form-control--password",
            icon: /*#__PURE__*/ React.createElement(ToggleIcon, {
                onClick: this.onVisibilityToggle,
                className: "password-toggle",
                isActive: passwordVisible
            }),
            errorMessage: errorMessage
        }, rest));
    }
    constructor(...args){
        super(...args);
        this.state = {
            passwordVisible: false
        };
        this.onVisibilityToggle = ()=>{
            this.setState((prevState)=>({
                    passwordVisible: !prevState.passwordVisible
                })
            );
        };
    }
}
Password.defaultProps = {
    required: true,
    validations: []
};

/* OWASP Special Characters: https://www.owasp.org/index.php/Password_special_characters */ const specialCharacters = [
    ' ',
    '!',
    '"',
    '#',
    '\\$',
    '%',
    '&',
    "'",
    '\\(',
    '\\)',
    '\\*',
    '\\+',
    ',',
    '-',
    '\\.',
    '/',
    ':',
    ';',
    '<',
    '=',
    '>',
    '\\?',
    '@',
    '\\[',
    '\\\\',
    '\\]',
    '\\^',
    '_',
    '`',
    '{',
    '\\|',
    '}',
    '~', 
].join('|');
const minLength = (password, amount)=>password.length >= amount
;
const containsLowercase = /[a-z]/;
const containsUppercase = /[A-Z]/;
const containsNumber = /[0-9]/;
const noTripleCharacters = /^(?:(.)\1?(?!\1))+$/;
const numberOfSpecialCharacters = (amount)=>new RegExp(`${specialCharacters}{${amount}}`)
;
// There must be three out of these four character types: lowercase, uppercase, number, symbol
const minRequiredCharacterTypes = 3;
const characterTypeRules = [
    containsLowercase,
    containsUppercase,
    containsNumber,
    numberOfSpecialCharacters(1), 
];
function isModerate(password) {
    // Moderate
    /**
   * At least 6 characters including 3 out of the following types:
   * - a lowercase letter
   * - an uppercase letter
   * - a number
   * - a special character (such as !@#$%^&*, see OWASP special characters at the top of this file)
   */ const rulesPassed = characterTypeRules.filter((rule)=>rule.test(password)
    );
    return minLength(password, 6) && rulesPassed.length >= minRequiredCharacterTypes;
}
function isStrong(password) {
    // Strong
    /**
   * At least 8 characters including 3 out of the following types:
   * - a lowercase letter
   * - an uppercase letter
   * - a number
   * - a special character (such as !@#$%^&*, see OWASP special characters at the top of this file)
   */ const rulesPassed = characterTypeRules.filter((rule)=>rule.test(password)
    );
    return minLength(password, 8) && rulesPassed.length >= minRequiredCharacterTypes;
}
function isExcellent(password) {
    // Excellent
    /**
   * At least 10 characters including 3 out of the following types:
   * - a lower-case letter
   * - an upper-case letter
   * - a number
   * - a special character (such as !@#$%^&*, see OWASP special characters at the top of this file)
   *
   * Not more than 2 identical characters in a row (e.g. 555 is not allowed)
   */ const rulesPassed = characterTypeRules.filter((rule)=>rule.test(password)
    );
    const passedNoTripleCharacters = noTripleCharacters.test(password);
    return minLength(password, 10) && passedNoTripleCharacters && rulesPassed.length >= minRequiredCharacterTypes;
}
function isWeak(password) {
    return !isModerate(password) && !isStrong(password) && !isExcellent(password);
}
function getPasswordStrength(password) {
    const measurements = [
        [
            'weak',
            isWeak(password)
        ],
        [
            'moderate',
            isModerate(password)
        ],
        [
            'strong',
            isStrong(password)
        ],
        [
            'excellent',
            isExcellent(password)
        ], 
    ];
    return measurements.reduce((strength, [key, value])=>value ? key : strength
    , 'weak');
}

const PasswordWithStrengthIndicator = (_param)=>{
    var { value , indicatorPosition ='bottom' , className  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "value",
        "indicatorPosition",
        "className"
    ]);
    const { getLabel  } = useIntl();
    const strength = getPasswordStrength(value);
    return /*#__PURE__*/ React.createElement("div", {
        "data-t": "password-with-strength-indicator",
        className: classnames('password-with-strength-indicator', `password-with-strength-indicator--position-${indicatorPosition}`, {
            'password-with-strength-indicator--with-label': rest.label
        }, className)
    }, /*#__PURE__*/ React.createElement(Password, swcHelpers.extends({}, rest, {
        "aria-describedby": "strength-indicator"
    })), /*#__PURE__*/ React.createElement("div", {
        id: "strength-indicator",
        className: classnames('password-with-strength-indicator__indicator', className)
    }, /*#__PURE__*/ React.createElement("p", {
        className: "password-with-strength-indicator__title"
    }, /*#__PURE__*/ React.createElement("span", null, getLabel('account.passwordStrength'), ': '), /*#__PURE__*/ React.createElement("span", {
        className: `password-with-strength-indicator__strength password-with-strength-indicator__strength--${strength}`
    }, getLabel(`account.passwordStrength.${strength}`))), /*#__PURE__*/ React.createElement("p", {
        className: "password-with-strength-indicator__message"
    }, getLabel(`account.passwordStrength.${strength}.hint`)), /*#__PURE__*/ React.createElement("div", {
        className: "password-with-strength-indicator__tooltip-arrow"
    }, /*#__PURE__*/ React.createElement("svg", {
        className: "tooltip-arrow__icon",
        viewBox: "0 0 120 160",
        "aria-hidden": true
    }, /*#__PURE__*/ React.createElement("path", {
        className: "tooltip-arrow__path",
        d: "M 20,80 150,20 150,140 Z"
    })))));
};

const PhoneNumber = (_param)=> {
    var { label , errorMessage  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "label",
        "errorMessage"
    ]);
    return React.createElement(ConnectedFormControl, swcHelpers.extends({}, ConnectedFormControl.defaultProps, {
        id: "phone",
        "data-t": "phone-number",
        type: "tel",
        label: label,
        errorMessage: errorMessage
    }, rest));
};

const PostalCode = (_param)=> {
    var { label , errorMessage  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "label",
        "errorMessage"
    ]);
    return React.createElement(ConnectedFormControl, swcHelpers.extends({}, ConnectedFormControl.defaultProps, {
        id: "postalCode",
        "data-t": "postal-code",
        label: label,
        errorMessage: errorMessage
    }, rest));
};

const Prefix = (_param)=> {
    var { label , errorMessage , validations =[]  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "label",
        "errorMessage",
        "validations"
    ]);
    return React.createElement(ConnectedFormControl, swcHelpers.extends({}, ConnectedFormControl.defaultProps, {
        id: "prefix",
        "data-t": "prefix",
        label: label,
        errorMessage: errorMessage,
        validations: validations
    }, rest));
};

const Region = (_param)=> {
    var { label , errorMessage , validations =[
        isText
    ]  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "label",
        "errorMessage",
        "validations"
    ]);
    return React.createElement(ConnectedFormControl, swcHelpers.extends({}, ConnectedFormControl.defaultProps, {
        id: "region",
        "data-t": "region",
        label: label,
        errorMessage: errorMessage,
        validations: validations
    }, rest));
};

const Street = (_param)=> {
    var { label , errorMessage , validations =[
        isRequired
    ]  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "label",
        "errorMessage",
        "validations"
    ]);
    return React.createElement(ConnectedFormControl, swcHelpers.extends({}, ConnectedFormControl.defaultProps, {
        id: "streetName",
        "data-t": "street",
        label: label,
        errorMessage: errorMessage,
        validations: validations
    }, rest));
};

const getFieldValues = (name, fields)=>{
    const day = fields[`${name}Day`] && fields[`${name}Day`].value;
    const month = fields[`${name}Month`] && fields[`${name}Month`].value;
    const year = fields[`${name}Year`] && fields[`${name}Year`].value;
    return {
        day,
        month,
        year
    };
};
const isEqualState = (a, b)=>a.value === b.value && a.isTouched === b.isTouched && a.isValid === b.isValid && a.readonly === b.readonly
;
const updateFields = (name, fields = {}, nextState = {})=>{
    if (Object.keys(fields).length === 0) {
        return {};
    }
    return swcHelpers.objectSpread({}, fields, {
        [name]: swcHelpers.objectSpread({}, fields[name], nextState, {
            name
        })
    });
};
const getFieldNames = (name)=>({
        day: `${name}Day`,
        month: `${name}Month`,
        year: `${name}Year`
    })
;
const joinValues = (name, fields)=>{
    const { day , month , year  } = getFieldValues(name, fields);
    if (!day || !month || !year) {
        return '';
    }
    return `${year}-${month}-${day}`;
};
const initializeFields = ({ name , value ='' , isTouched =false , required =false  })=>{
    var ref;
    const date = (ref = value.split('-')) !== null && ref !== void 0 ? ref : '';
    var ref1;
    const day = {
        name: `${name}Day`,
        value: (ref1 = date[2]) !== null && ref1 !== void 0 ? ref1 : ''
    };
    var ref2;
    const month = {
        name: `${name}Month`,
        value: (ref2 = date[1]) !== null && ref2 !== void 0 ? ref2 : ''
    };
    var ref3;
    const year = {
        name: `${name}Year`,
        value: (ref3 = date[0]) !== null && ref3 !== void 0 ? ref3 : ''
    };
    return {
        [year.name]: {
            name: year.name,
            value: year.value,
            isTouched: isTouched || !!year.value,
            isValid: validateWithRequired(year.value, isValidBirthDateYear, {
                required
            })
        },
        [month.name]: {
            name: month.name,
            value: month.value,
            isTouched: isTouched || !!month.value,
            isValid: validateWithRequired(month.value, isValidMonth, {
                required
            })
        },
        [day.name]: {
            name: day.name,
            value: day.value,
            isTouched: isTouched || !!day.value,
            isValid: validateWithRequired(day.value, isValidDay, {
                required
            })
        }
    };
};
const validateFullDate = (name, fields, required, validation)=>{
    if (!fields) {
        return false;
    }
    const { day , month , year  } = getFieldValues(name, fields);
    // Field is valid when it's not required and not all of the fields have values
    if (!required && (day === '' || month === '' || year === '')) {
        return true;
    }
    const value1 = [
        year,
        month,
        day
    ].filter((value)=>!!value
    ).join('-');
    return !validation || FormControl.validate([
        validation
    ], required, value1);
};
const focusElementWhenValueReady = (value, ref)=>{
    var ref4;
    return value.length === 2 ? (ref4 = ref.current) === null || ref4 === void 0 ? void 0 : ref4.focus() : undefined;
};
const getErrorMessageBasedOnValidation = (validationResults, errorMessages = [])=>{
    const indexOfFirstFailedValidation = validationResults.lastIndexOf(false);
    if (indexOfFirstFailedValidation === -1) {
        return '';
    }
    return errorMessages[indexOfFirstFailedValidation];
};
const validateWithRequired = (value, validator, options)=>{
    if (!(options === null || options === void 0 ? void 0 : options.required)) {
        return !value ? true : validator(value);
    }
    return validator(value);
};

const Date$1 = (_param)=>{
    var { name: name1 , label , validations , disabled =false , required =false , initialState: initialState1 , additionalScreenReaderLabel , tooltipText , hideAsteriskSign , fieldLabels , onChange , onBlur , className  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "name",
        "label",
        "validations",
        "disabled",
        "required",
        "initialState",
        "additionalScreenReaderLabel",
        "tooltipText",
        "hideAsteriskSign",
        "fieldLabels",
        "onChange",
        "onBlur",
        "className"
    ]);
    var ref11, ref1, ref2, ref3, ref4, ref5;
    const [validationResults, setValidationResults] = React.useState([]);
    const [isFullDateValid, setIsFullDateValid] = React.useState(true);
    const { DATES_RANGE  } = validationsUtil;
    var ref6;
    const [state1, setState] = React.useState({
        name: name1,
        fields: {},
        isTouched: !!(initialState1 === null || initialState1 === void 0 ? void 0 : initialState1.isTouched),
        isValid: !!(initialState1 === null || initialState1 === void 0 ? void 0 : initialState1.isValid),
        value: (ref6 = initialState1 === null || initialState1 === void 0 ? void 0 : initialState1.value) !== null && ref6 !== void 0 ? ref6 : ''
    });
    const { getLabel  } = useIntl();
    const formContext = React.useContext(FormContext);
    const monthRef = /*#__PURE__*/ React.createRef();
    const yearRef = /*#__PURE__*/ React.createRef();
    /** Get initial state of the field on mount */ React.useEffect(()=>{
        var /** Pass initial state to the form context */ ref;
        const fields = initializeFields({
            name: name1,
            value: state1.value,
            required
        });
        setState(swcHelpers.objectSpread({}, state1, {
            value: state1.value,
            fields
        }));
        (ref = formContext.onChange) === null || ref === void 0 ? void 0 : ref.call(formContext, swcHelpers.objectSpread({}, state1, {
            value: state1.value
        }));
        onChange === null || onChange === void 0 ? void 0 : onChange(swcHelpers.objectSpread({}, state1, {
            value: state1.value
        }));
    }, []);
    React.useEffect(()=>{
        var ref;
        const initialState = (ref = formContext.getInitialState) === null || ref === void 0 ? void 0 : ref.call(formContext, name1);
        const fields = initializeFields({
            name: name1,
            value: initialState === null || initialState === void 0 ? void 0 : initialState.value,
            isTouched: initialState === null || initialState === void 0 ? void 0 : initialState.isTouched,
            required
        });
        var ref12;
        const newState = swcHelpers.objectSpread({}, state1, {
            fields,
            value: (ref12 = initialState === null || initialState === void 0 ? void 0 : initialState.value) !== null && ref12 !== void 0 ? ref12 : '',
            isTouched: !!(initialState === null || initialState === void 0 ? void 0 : initialState.isTouched),
            isValid: handleValidateFullDate(fields)
        });
        /** Update state only if form context data changed */ if (!isEqualState(state1, newState)) {
            var ref13;
            setState(newState);
            (ref13 = formContext.onChange) === null || ref13 === void 0 ? void 0 : ref13.call(formContext, newState);
            onChange === null || onChange === void 0 ? void 0 : onChange(newState);
        }
    /** Run only on form context data changed */ // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        formContext
    ]);
    const handleValidateFullDate = React.useCallback((fields)=>{
        if (!validations) {
            return true;
        }
        const validateFullDateResults = validations.map(({ validator  })=>validateFullDate(name1, fields, required || false, validator)
        );
        const isValid = validateFullDateResults.indexOf(false) === -1;
        setValidationResults(validateFullDateResults);
        setIsFullDateValid(isValid);
        return isValid;
    }, [
        name1,
        required,
        validations
    ]);
    const handleInputChange = React.useCallback(({ state: inputState , name: inputName , validator , ref  })=>{
        var /** Update form context with the new state after field value is changes */ ref14;
        /** Get new state fields based on new value */ const fields = updateFields(inputName, state1.fields, {
            value: inputState.value,
            isValid: validateWithRequired(inputState.value, validator, {
                required
            })
        });
        /** Get new state based on new fields and value */ const formState = swcHelpers.objectSpread({}, state1, {
            fields,
            value: joinValues(name1, fields),
            isValid: handleValidateFullDate(fields),
            name: name1
        });
        /** If ref provided jump to the next field if the current field has a valid value */ ref && focusElementWhenValueReady(inputState.value, ref);
        setState(formState);
        (ref14 = formContext.onChange) === null || ref14 === void 0 ? void 0 : ref14.call(formContext, formState);
        onChange === null || onChange === void 0 ? void 0 : onChange(formState);
    }, [
        state1,
        formContext,
        name1,
        required,
        onChange,
        handleValidateFullDate
    ]);
    /** On blur touch current field and the whole field too */ const handleBlur = React.useCallback((name)=>{
        var ref, ref15, ref16;
        var ref17;
        const fields = updateFields(name, state1.fields, {
            value: (ref17 = (ref = state1.fields) === null || ref === void 0 ? void 0 : (ref15 = ref[name]) === null || ref15 === void 0 ? void 0 : ref15.value) !== null && ref17 !== void 0 ? ref17 : '',
            isTouched: true
        });
        /** Touch the entire date state when all fields are touched */ const isAllFieldsTouched = !Object.values(fields !== null && fields !== void 0 ? fields : {}).some(({ isTouched  })=>!isTouched
        );
        const newState = swcHelpers.objectSpread({}, state1, {
            fields,
            isTouched: isAllFieldsTouched
        });
        setState(newState);
        (ref16 = formContext.onBlur) === null || ref16 === void 0 ? void 0 : ref16.call(formContext, newState);
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(newState);
    }, [
        onBlur,
        formContext,
        state1
    ]);
    var ref7;
    const isAllFieldsValid = React.useMemo(()=>{
        return !Object.values((ref7 = state1 === null || state1 === void 0 ? void 0 : state1.fields) !== null && ref7 !== void 0 ? ref7 : {}).some(({ isValid  })=>!isValid
        );
    }, [
        state1
    ]);
    const applyClasses = React.useCallback((name)=>{
        var ref;
        const field = (ref = state1.fields) === null || ref === void 0 ? void 0 : ref[name];
        /** Return based class for the input id no field */ if (!field) {
            return 'form-control__input';
        }
        /** Return invalid class if only full date invalid.
       * E.g. 1994-02-31 the full date is invalid, but technically each of the fields are valid.
       */ if (isAllFieldsValid && !isFullDateValid && state1.isTouched === true) {
            return 'form-control__input _is_invalid';
        }
        return classnames('form-control__input', {
            _is_valid: (field === null || field === void 0 ? void 0 : field.value) && (field === null || field === void 0 ? void 0 : field.isValid) && (field === null || field === void 0 ? void 0 : field.isTouched) === true,
            _is_invalid: !(field === null || field === void 0 ? void 0 : field.isValid) && (field === null || field === void 0 ? void 0 : field.isTouched) === true
        });
    }, [
        isAllFieldsValid,
        isFullDateValid,
        state1
    ]);
    const { day , month , year  } = getFieldNames(name1);
    const handleOnDayChange = React.useCallback((state)=>{
        handleInputChange({
            state,
            name: day,
            validator: isValidDay,
            ref: monthRef
        });
    }, [
        day,
        monthRef,
        handleInputChange
    ]);
    const handleOnMonthChange = React.useCallback((state)=>{
        handleInputChange({
            state,
            name: month,
            validator: isValidMonth,
            ref: yearRef
        });
    }, [
        month,
        yearRef,
        handleInputChange
    ]);
    const handleOnYearChange = React.useCallback((state)=>{
        handleInputChange({
            state,
            name: year,
            validator: isValidBirthDateYear
        });
    }, [
        year,
        handleInputChange
    ]);
    const handleOnDayBlur = React.useCallback(()=>handleBlur(day)
    , [
        day,
        handleBlur
    ]);
    const handleOnMonthBlur = React.useCallback(()=>handleBlur(month)
    , [
        month,
        handleBlur
    ]);
    const handleOnYearBlur = React.useCallback(()=>handleBlur(year)
    , [
        year,
        handleBlur
    ]);
    const showAsterisk = required && !hideAsteriskSign;
    const showTopContainer = !!additionalScreenReaderLabel || showAsterisk || !!tooltipText;
    var ref8, ref9, ref10;
    return /*#__PURE__*/ React.createElement("fieldset", swcHelpers.extends({}, rest, {
        name: name1,
        className: classnames('date-field', className),
        "aria-describedby": "additionalScreenReaderLabel"
    }), label && /*#__PURE__*/ React.createElement("legend", {
        className: "date-field__legend"
    }, label), showTopContainer && /*#__PURE__*/ React.createElement("div", {
        className: "date-field__top-container"
    }, additionalScreenReaderLabel && /*#__PURE__*/ React.createElement("span", {
        className: "date-field__additional-screen-reader-label",
        id: "additionalScreenReaderLabel"
    }, additionalScreenReaderLabel), showAsterisk && /*#__PURE__*/ React.createElement("span", {
        className: "date-field__asterisk"
    }, "*"), tooltipText && /*#__PURE__*/ React.createElement(Tooltip, {
        id: `t_${name1}`,
        message: tooltipText
    })), /*#__PURE__*/ React.createElement("div", {
        className: "date-field__container"
    }, /*#__PURE__*/ React.createElement("label", {
        className: "date-field__label date-field__label__day",
        htmlFor: day
    }, (fieldLabels === null || fieldLabels === void 0 ? void 0 : fieldLabels[day]) && fieldLabels[day], /*#__PURE__*/ React.createElement(Input, {
        id: day,
        placeholder: getLabel('common.placeholder.day.abbr'),
        type: "number",
        min: DATES_RANGE.DAY.min,
        max: DATES_RANGE.DAY.max,
        className: applyClasses(day),
        disabled: disabled,
        onChange: handleOnDayChange,
        onBlur: handleOnDayBlur,
        initialValue: (ref8 = state1 === null || state1 === void 0 ? void 0 : (ref11 = state1.fields) === null || ref11 === void 0 ? void 0 : (ref1 = ref11[day]) === null || ref1 === void 0 ? void 0 : ref1.value) !== null && ref8 !== void 0 ? ref8 : '',
        pattern: "\\d*"
    })), /*#__PURE__*/ React.createElement("label", {
        className: "date-field__label date-field__label__month",
        htmlFor: month
    }, (fieldLabels === null || fieldLabels === void 0 ? void 0 : fieldLabels[month]) && fieldLabels[month], /*#__PURE__*/ React.createElement(Input, {
        id: month,
        placeholder: getLabel('common.placeholder.month.abbr'),
        type: "number",
        min: DATES_RANGE.MONTH.min,
        max: DATES_RANGE.MONTH.max,
        className: applyClasses(month),
        disabled: disabled,
        inputRef: monthRef,
        onChange: handleOnMonthChange,
        onBlur: handleOnMonthBlur,
        initialValue: (ref9 = state1 === null || state1 === void 0 ? void 0 : (ref2 = state1.fields) === null || ref2 === void 0 ? void 0 : (ref3 = ref2[month]) === null || ref3 === void 0 ? void 0 : ref3.value) !== null && ref9 !== void 0 ? ref9 : '',
        pattern: "\\d*"
    })), /*#__PURE__*/ React.createElement("label", {
        className: "date-field__label date-field__label__year",
        htmlFor: year
    }, (fieldLabels === null || fieldLabels === void 0 ? void 0 : fieldLabels[year]) && fieldLabels[year], /*#__PURE__*/ React.createElement(Input, {
        id: year,
        placeholder: getLabel('common.placeholder.year.abbr'),
        type: "number",
        min: 1,
        className: applyClasses(year),
        disabled: disabled,
        inputRef: yearRef,
        onChange: handleOnYearChange,
        onBlur: handleOnYearBlur,
        initialValue: (ref10 = state1 === null || state1 === void 0 ? void 0 : (ref4 = state1.fields) === null || ref4 === void 0 ? void 0 : (ref5 = ref4[year]) === null || ref5 === void 0 ? void 0 : ref5.value) !== null && ref10 !== void 0 ? ref10 : '',
        pattern: "\\d*"
    }))), state1.isTouched && !state1.isValid && /*#__PURE__*/ React.createElement("span", {
        className: "date-field__error"
    }, getErrorMessageBasedOnValidation(validationResults, validations === null || validations === void 0 ? void 0 : validations.map(({ errorMessage  })=>errorMessage
    ))));
};

const Number$1 = (_param)=> {
    var { validations =[
        isNumber
    ]  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "validations"
    ]);
    return React.createElement(ConnectedFormControl, swcHelpers.extends({}, ConnectedFormControl.defaultProps, {
        validations: validations
    }, rest));
};

const Text = (_param)=> {
    var { validations =[
        isText
    ]  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "validations"
    ]);
    return React.createElement(ConnectedFormControl, swcHelpers.extends({}, ConnectedFormControl.defaultProps, {
        validations: validations
    }, rest));
};

export { AddressLine, BookAppointment, CheckboxView$1 as CheckboxView, CheckboxWithLabel, City, ComposedForm, ConnectedRadioButtons, ConnectedContactMethodContainer as ContactMethod, Date$1 as Date, ConnectedDateOfBirthSelect as DateOfBirthSelect, DatePicker, DiscountCodeForm, Email, ErrorMessage$1 as ErrorMessage, Field, FirstName, Form, ConnectedFormControl as FormControl, FormGroup, types as FormTypes, HouseNumber, HouseNumberSuffix, Input, LastName, MiddleName, ConnectedMultipleAddressLine as MultipleAddressLine, Number$1 as Number, Password, PasswordWithStrengthIndicator, PhoneNumber, PostalCode, Prefix, ConnectedPrefixSelect as PrefixSelect, RadioButtons, RangeDatePicker, RecaptchaContext, RecaptchaProvider, Region, RequiredFieldsLabel, Select, Street, Suggestions, Text, TextArea, index as Util, useFormContext, useRecaptcha };
//# sourceMappingURL=index.js.map
